import { useMutation, useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { GraphQLClient } from 'graphql-request';

// src/generated/index.ts

// src/constants.ts
var API_URL_LOCAL = "http://localhost:8080/v1/graphql";
var API_URL_DEV = "https://prod.base-sepolia.intuition.sh/v1/graphql";
var API_URL_PROD = "https://prod.base-mainnet-v-1-0.intuition.sh/v1/graphql";

// src/client.ts
var DEFAULT_API_URL = API_URL_PROD;
var globalConfig = {
  apiUrl: DEFAULT_API_URL
};
function configureClient(config) {
  globalConfig = { ...globalConfig, ...config };
}
function getClientConfig(token) {
  return {
    headers: {
      ...token && { authorization: `Bearer ${token}` },
      "Content-Type": "application/json"
    },
    apiUrl: globalConfig.apiUrl
  };
}
function createServerClient({ token }) {
  const config = getClientConfig(token);
  if (!config.apiUrl) {
    throw new Error(
      "GraphQL API URL not configured. Call configureClient first."
    );
  }
  return new GraphQLClient(config.apiUrl, config);
}
var fetchParams = () => {
  return {
    headers: {
      "Content-Type": "application/json; charset=utf-8"
    }
  };
};
function fetcher(query, variables, options) {
  return async () => {
    if (!globalConfig.apiUrl) {
      throw new Error(
        "GraphQL API URL not configured. Call configureClient first."
      );
    }
    const res = await fetch(globalConfig.apiUrl, {
      method: "POST",
      ...fetchParams(),
      ...options,
      body: JSON.stringify({ query, variables })
    });
    const json = await res.json();
    if (json.errors && (!json.data || Object.keys(json.data).length === 0)) {
      const { message } = json.errors[0];
      throw new Error(message);
    }
    return json.data;
  };
}

// src/generated/index.ts
var AccountPositionsAggregateFragmentDoc = `
    fragment AccountPositionsAggregate on accounts {
  positions_aggregate(order_by: {shares: desc}) {
    aggregate {
      count
    }
    nodes {
      id
      shares
      vault {
        term_id
        total_shares
        current_share_price
        term {
          atom {
            term_id
            label
          }
          triple {
            term_id
          }
        }
      }
    }
  }
}
    `;
var AccountPositionsFragmentDoc = `
    fragment AccountPositions on accounts {
  positions(
    order_by: {shares: desc}
    limit: $positionsLimit
    offset: $positionsOffset
    where: $positionsWhere
  ) {
    id
    shares
    vault {
      term_id
      total_shares
      current_share_price
      term {
        atom {
          term_id
          label
        }
        triple {
          term_id
        }
      }
    }
  }
}
    `;
var AccountAtomsFragmentDoc = `
    fragment AccountAtoms on accounts {
  atoms(
    where: $atomsWhere
    order_by: $atomsOrderBy
    limit: $atomsLimit
    offset: $atomsOffset
  ) {
    term_id
    label
    data
    term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        total_shares
        positions_aggregate(where: {account_id: {_eq: $address}}) {
          nodes {
            account {
              id
            }
            shares
          }
        }
      }
    }
  }
}
    `;
var AccountAtomsAggregateFragmentDoc = `
    fragment AccountAtomsAggregate on accounts {
  atoms_aggregate(
    where: $atomsWhere
    order_by: $atomsOrderBy
    limit: $atomsLimit
    offset: $atomsOffset
  ) {
    aggregate {
      count
      sum {
        term_id
      }
    }
    nodes {
      term_id
      label
      data
      term {
        vaults(where: {curve_id: {_eq: "1"}}) {
          total_shares
          positions_aggregate(where: {account_id: {_eq: $address}}) {
            nodes {
              account {
                id
              }
              shares
            }
          }
        }
      }
    }
  }
}
    `;
var AccountTriplesFragmentDoc = `
    fragment AccountTriples on accounts {
  triples_aggregate(
    where: $triplesWhere
    order_by: $triplesOrderBy
    limit: $triplesLimit
    offset: $triplesOffset
  ) {
    aggregate {
      count
    }
    nodes {
      term_id
      subject {
        term_id
        label
      }
      predicate {
        term_id
        label
      }
      object {
        term_id
        label
      }
    }
  }
}
    `;
var AccountTriplesAggregateFragmentDoc = `
    fragment AccountTriplesAggregate on accounts {
  triples_aggregate(
    where: $triplesWhere
    order_by: $triplesOrderBy
    limit: $triplesLimit
    offset: $triplesOffset
  ) {
    aggregate {
      count
    }
    nodes {
      term_id
      subject {
        term_id
        label
      }
      predicate {
        term_id
        label
      }
      object {
        term_id
        label
      }
    }
  }
}
    `;
var AtomTxnFragmentDoc = `
    fragment AtomTxn on atoms {
  block_number
  created_at
  transaction_hash
  creator_id
}
    `;
var AtomVaultDetailsFragmentDoc = `
    fragment AtomVaultDetails on atoms {
  term_id
  wallet_id
  term {
    vaults(where: {curve_id: {_eq: "1"}}) {
      position_count
      total_shares
      current_share_price
      positions_aggregate {
        aggregate {
          count
          sum {
            shares
          }
        }
      }
      positions {
        id
        account {
          label
          id
        }
        shares
      }
    }
  }
}
    `;
var AccountMetadataFragmentDoc = `
    fragment AccountMetadata on accounts {
  label
  image
  id
  atom_id
  type
}
    `;
var AtomTripleFragmentDoc = `
    fragment AtomTriple on atoms {
  as_subject_triples {
    term_id
    object {
      data
      term_id
      image
      label
      emoji
      type
      creator {
        ...AccountMetadata
      }
    }
    predicate {
      data
      term_id
      image
      label
      emoji
      type
      creator {
        ...AccountMetadata
      }
    }
  }
  as_predicate_triples {
    term_id
    subject {
      data
      term_id
      image
      label
      emoji
      type
      creator {
        ...AccountMetadata
      }
    }
    object {
      data
      term_id
      image
      label
      emoji
      type
      creator {
        ...AccountMetadata
      }
    }
  }
  as_object_triples {
    term_id
    subject {
      data
      term_id
      image
      label
      emoji
      type
      creator {
        ...AccountMetadata
      }
    }
    predicate {
      data
      term_id
      image
      label
      emoji
      type
      creator {
        ...AccountMetadata
      }
    }
  }
}
    `;
var AtomVaultDetailsWithPositionsFragmentDoc = `
    fragment AtomVaultDetailsWithPositions on atoms {
  term {
    vaults(where: {curve_id: {_eq: "1"}}) {
      total_shares
      current_share_price
      positions_aggregate(where: {account_id: {_in: $addresses}}) {
        aggregate {
          sum {
            shares
          }
        }
        nodes {
          account {
            id
          }
          shares
        }
      }
    }
  }
}
    `;
var DepositEventFragmentFragmentDoc = `
    fragment DepositEventFragment on events {
  deposit {
    term_id
    curve_id
    sender_assets_after_total_fees
    shares_for_receiver
    receiver {
      id
    }
    sender {
      id
    }
  }
}
    `;
var RedemptionEventFragmentFragmentDoc = `
    fragment RedemptionEventFragment on events {
  redemption {
    term_id
    curve_id
    receiver_id
    shares_redeemed_by_sender
    assets_for_receiver
  }
}
    `;
var AtomValueFragmentDoc = `
    fragment AtomValue on atoms {
  value {
    person {
      name
      image
      description
      url
    }
    thing {
      name
      image
      description
      url
    }
    organization {
      name
      image
      description
      url
    }
    account {
      id
      label
      image
    }
  }
}
    `;
var AtomMetadataFragmentDoc = `
    fragment AtomMetadata on atoms {
  term_id
  data
  image
  label
  emoji
  type
  wallet_id
  creator {
    id
    label
    image
  }
  ...AtomValue
}
    `;
var PositionAggregateFieldsFragmentDoc = `
    fragment PositionAggregateFields on positions_aggregate {
  aggregate {
    count
    sum {
      shares
    }
  }
}
    `;
var PositionFieldsFragmentDoc = `
    fragment PositionFields on positions {
  account {
    id
    label
  }
  shares
  vault {
    term_id
    total_shares
    current_share_price
  }
}
    `;
var TripleMetadataFragmentDoc = `
    fragment TripleMetadata on triples {
  term_id
  subject_id
  predicate_id
  object_id
  subject {
    data
    term_id
    image
    label
    emoji
    type
    ...AtomValue
    creator {
      ...AccountMetadata
    }
  }
  predicate {
    data
    term_id
    image
    label
    emoji
    type
    ...AtomValue
    creator {
      ...AccountMetadata
    }
  }
  object {
    data
    term_id
    image
    label
    emoji
    type
    ...AtomValue
    creator {
      ...AccountMetadata
    }
  }
  term {
    vaults(where: {curve_id: {_eq: "1"}}) {
      total_shares
      current_share_price
      allPositions: positions_aggregate {
        ...PositionAggregateFields
      }
      positions {
        ...PositionFields
      }
    }
  }
  counter_term {
    vaults(where: {curve_id: {_eq: "1"}}) {
      total_shares
      current_share_price
      allPositions: positions_aggregate {
        ...PositionAggregateFields
      }
      positions {
        ...PositionFields
      }
    }
  }
}
    `;
var EventDetailsFragmentDoc = `
    fragment EventDetails on events {
  block_number
  created_at
  type
  transaction_hash
  atom_id
  triple_id
  deposit_id
  redemption_id
  ...DepositEventFragment
  ...RedemptionEventFragment
  atom {
    ...AtomMetadata
    term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        total_shares
        position_count
        positions {
          account_id
          shares
          account {
            id
            label
            image
          }
        }
      }
    }
  }
  triple {
    ...TripleMetadata
    term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        total_shares
        position_count
        positions {
          account_id
          shares
          account {
            id
            label
            image
          }
        }
      }
    }
    counter_term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        total_shares
        position_count
        positions {
          account_id
          shares
          account {
            id
            label
            image
          }
        }
      }
    }
  }
}
    `;
var FollowMetadataFragmentDoc = `
    fragment FollowMetadata on triples {
  term_id
  subject {
    term_id
    label
  }
  predicate {
    term_id
    label
  }
  object {
    term_id
    label
  }
  term {
    vaults(where: {curve_id: {_eq: "1"}}) {
      term_id
      total_shares
      current_share_price
      positions_aggregate(where: $positionsWhere) {
        aggregate {
          count
          sum {
            shares
          }
        }
      }
      positions(
        limit: $positionsLimit
        offset: $positionsOffset
        order_by: $positionsOrderBy
        where: $positionsWhere
      ) {
        account {
          id
          label
        }
        shares
      }
    }
  }
}
    `;
var FollowAggregateFragmentDoc = `
    fragment FollowAggregate on triples_aggregate {
  aggregate {
    count
  }
}
    `;
var StatDetailsFragmentDoc = `
    fragment StatDetails on stats {
  contract_balance
  total_accounts
  total_fees
  total_atoms
  total_triples
  total_positions
  total_signals
}
    `;
var TripleTxnFragmentDoc = `
    fragment TripleTxn on triples {
  block_number
  created_at
  transaction_hash
  creator_id
}
    `;
var PositionDetailsFragmentDoc = `
    fragment PositionDetails on positions {
  id
  account {
    id
    label
    image
  }
  vault {
    term_id
    term {
      atom {
        term_id
        label
        image
      }
      triple {
        term_id
        term {
          vaults(where: {curve_id: {_eq: "1"}}) {
            term_id
            position_count
            positions_aggregate {
              aggregate {
                sum {
                  shares
                }
              }
            }
          }
        }
        counter_term {
          vaults(where: {curve_id: {_eq: "1"}}) {
            term_id
            position_count
            positions_aggregate {
              aggregate {
                sum {
                  shares
                }
              }
            }
          }
        }
        subject {
          data
          term_id
          label
          image
          emoji
          type
          ...AtomValue
          creator {
            ...AccountMetadata
          }
        }
        predicate {
          data
          term_id
          label
          image
          emoji
          type
          ...AtomValue
          creator {
            ...AccountMetadata
          }
        }
        object {
          data
          term_id
          label
          image
          emoji
          type
          ...AtomValue
          creator {
            ...AccountMetadata
          }
        }
      }
    }
  }
  shares
  term_id
  curve_id
}
    `;
var TripleVaultDetailsFragmentDoc = `
    fragment TripleVaultDetails on triples {
  term_id
  counter_term_id
  term {
    vaults(where: {curve_id: {_eq: "1"}}) {
      positions {
        ...PositionDetails
      }
    }
  }
  counter_term {
    vaults(where: {curve_id: {_eq: "1"}}) {
      positions {
        ...PositionDetails
      }
    }
  }
}
    `;
var VaultBasicDetailsFragmentDoc = `
    fragment VaultBasicDetails on vaults {
  term_id
  curve_id
  term {
    atom {
      term_id
      label
    }
    triple {
      term_id
      subject {
        term_id
        label
      }
      predicate {
        term_id
        label
      }
      object {
        term_id
        label
      }
    }
  }
  current_share_price
  total_shares
}
    `;
var VaultFilteredPositionsFragmentDoc = `
    fragment VaultFilteredPositions on vaults {
  positions(where: {account_id: {_in: $addresses}}) {
    ...PositionFields
  }
}
    `;
var VaultDetailsWithFilteredPositionsFragmentDoc = `
    fragment VaultDetailsWithFilteredPositions on vaults {
  ...VaultBasicDetails
  ...VaultFilteredPositions
}
    `;
var TripleVaultCouterVaultDetailsWithPositionsFragmentDoc = `
    fragment TripleVaultCouterVaultDetailsWithPositions on triples {
  term_id
  counter_term_id
  term {
    vaults(where: {curve_id: {_eq: "1"}}) {
      ...VaultDetailsWithFilteredPositions
    }
  }
  counter_term {
    vaults(where: {curve_id: {_eq: "1"}}) {
      ...VaultDetailsWithFilteredPositions
    }
  }
}
    `;
var VaultUnfilteredPositionsFragmentDoc = `
    fragment VaultUnfilteredPositions on vaults {
  positions {
    ...PositionFields
  }
}
    `;
var VaultDetailsFragmentDoc = `
    fragment VaultDetails on vaults {
  ...VaultBasicDetails
}
    `;
var VaultPositionsAggregateFragmentDoc = `
    fragment VaultPositionsAggregate on vaults {
  positions_aggregate {
    ...PositionAggregateFields
  }
}
    `;
var VaultFieldsForTripleFragmentDoc = `
    fragment VaultFieldsForTriple on vaults {
  total_shares
  current_share_price
  ...VaultPositionsAggregate
  ...VaultFilteredPositions
}
    `;
var AtomMetadataMaybedeletethisFragmentDoc = `
    fragment AtomMetadataMAYBEDELETETHIS on atoms {
  term_id
  data
  image
  label
  emoji
  type
  wallet_id
  creator {
    id
    label
    image
  }
  ...AtomValue
}
    `;
var PinThingDocument = `
    mutation pinThing($name: String!, $description: String, $image: String, $url: String) {
  pinThing(
    thing: {description: $description, image: $image, name: $name, url: $url}
  ) {
    uri
  }
}
    `;
var usePinThingMutation = (options) => {
  return useMutation({
    mutationKey: ["pinThing"],
    mutationFn: (variables) => fetcher(
      PinThingDocument,
      variables
    )(),
    ...options
  });
};
usePinThingMutation.getKey = () => ["pinThing"];
usePinThingMutation.fetcher = (variables, options) => fetcher(
  PinThingDocument,
  variables,
  options
);
var GetAccountsDocument = `
    query GetAccounts($limit: Int, $offset: Int, $orderBy: [accounts_order_by!], $where: accounts_bool_exp, $positionsLimit: Int, $positionsOffset: Int, $positionsWhere: positions_bool_exp) {
  accounts(limit: $limit, offset: $offset, order_by: $orderBy, where: $where) {
    ...AccountMetadata
    ...AccountPositions
    atom {
      term_id
      wallet_id
      term {
        vaults(where: {curve_id: {_eq: "1"}}) {
          position_count
          total_shares
          positions_aggregate {
            aggregate {
              count
              sum {
                shares
              }
            }
          }
          positions {
            id
            account {
              label
              id
            }
            shares
          }
        }
      }
    }
  }
}
    ${AccountMetadataFragmentDoc}
${AccountPositionsFragmentDoc}`;
var useGetAccountsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetAccounts"] : ["GetAccounts", variables],
    queryFn: fetcher(
      GetAccountsDocument,
      variables
    ),
    ...options
  });
};
useGetAccountsQuery.document = GetAccountsDocument;
useGetAccountsQuery.getKey = (variables) => variables === void 0 ? ["GetAccounts"] : ["GetAccounts", variables];
var useInfiniteGetAccountsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetAccounts.infinite"] : ["GetAccounts.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetAccountsDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAccountsQuery.getKey = (variables) => variables === void 0 ? ["GetAccounts.infinite"] : ["GetAccounts.infinite", variables];
useGetAccountsQuery.fetcher = (variables, options) => fetcher(
  GetAccountsDocument,
  variables,
  options
);
var GetAccountsWithAggregatesDocument = `
    query GetAccountsWithAggregates($limit: Int, $offset: Int, $orderBy: [accounts_order_by!], $where: accounts_bool_exp, $positionsLimit: Int, $positionsOffset: Int, $positionsWhere: positions_bool_exp, $atomsWhere: atoms_bool_exp, $atomsOrderBy: [atoms_order_by!], $atomsLimit: Int, $atomsOffset: Int) {
  accounts_aggregate(
    limit: $limit
    offset: $offset
    order_by: $orderBy
    where: $where
  ) {
    aggregate {
      count
    }
    nodes {
      ...AccountMetadata
      ...AccountPositions
    }
  }
}
    ${AccountMetadataFragmentDoc}
${AccountPositionsFragmentDoc}`;
var useGetAccountsWithAggregatesQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetAccountsWithAggregates"] : ["GetAccountsWithAggregates", variables],
    queryFn: fetcher(GetAccountsWithAggregatesDocument, variables),
    ...options
  });
};
useGetAccountsWithAggregatesQuery.document = GetAccountsWithAggregatesDocument;
useGetAccountsWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetAccountsWithAggregates"] : ["GetAccountsWithAggregates", variables];
var useInfiniteGetAccountsWithAggregatesQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetAccountsWithAggregates.infinite"] : ["GetAccountsWithAggregates.infinite", variables],
        queryFn: (metaData) => fetcher(GetAccountsWithAggregatesDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAccountsWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetAccountsWithAggregates.infinite"] : ["GetAccountsWithAggregates.infinite", variables];
useGetAccountsWithAggregatesQuery.fetcher = (variables, options) => fetcher(GetAccountsWithAggregatesDocument, variables, options);
var GetAccountsCountDocument = `
    query GetAccountsCount($where: accounts_bool_exp) {
  accounts_aggregate(where: $where) {
    aggregate {
      count
    }
  }
}
    `;
var useGetAccountsCountQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetAccountsCount"] : ["GetAccountsCount", variables],
    queryFn: fetcher(
      GetAccountsCountDocument,
      variables
    ),
    ...options
  });
};
useGetAccountsCountQuery.document = GetAccountsCountDocument;
useGetAccountsCountQuery.getKey = (variables) => variables === void 0 ? ["GetAccountsCount"] : ["GetAccountsCount", variables];
var useInfiniteGetAccountsCountQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetAccountsCount.infinite"] : ["GetAccountsCount.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetAccountsCountDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAccountsCountQuery.getKey = (variables) => variables === void 0 ? ["GetAccountsCount.infinite"] : ["GetAccountsCount.infinite", variables];
useGetAccountsCountQuery.fetcher = (variables, options) => fetcher(
  GetAccountsCountDocument,
  variables,
  options
);
var GetAccountDocument = `
    query GetAccount($address: String!, $positionsLimit: Int, $positionsOffset: Int, $positionsWhere: positions_bool_exp, $atomsWhere: atoms_bool_exp, $atomsOrderBy: [atoms_order_by!], $atomsLimit: Int, $atomsOffset: Int, $triplesWhere: triples_bool_exp, $triplesOrderBy: [triples_order_by!], $triplesLimit: Int, $triplesOffset: Int) {
  account(id: $address) {
    ...AccountMetadata
    atom {
      ...AtomMetadata
      ...AtomVaultDetails
    }
    ...AccountPositions
    ...AccountAtoms
    ...AccountTriples
  }
  chainlink_prices(limit: 1, order_by: {id: desc}) {
    usd
  }
}
    ${AccountMetadataFragmentDoc}
${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AtomVaultDetailsFragmentDoc}
${AccountPositionsFragmentDoc}
${AccountAtomsFragmentDoc}
${AccountTriplesFragmentDoc}`;
var useGetAccountQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetAccount", variables],
    queryFn: fetcher(
      GetAccountDocument,
      variables
    ),
    ...options
  });
};
useGetAccountQuery.document = GetAccountDocument;
useGetAccountQuery.getKey = (variables) => [
  "GetAccount",
  variables
];
var useInfiniteGetAccountQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetAccount.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetAccountDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAccountQuery.getKey = (variables) => [
  "GetAccount.infinite",
  variables
];
useGetAccountQuery.fetcher = (variables, options) => fetcher(
  GetAccountDocument,
  variables,
  options
);
var GetAccountWithPaginatedRelationsDocument = `
    query GetAccountWithPaginatedRelations($address: String!, $positionsLimit: Int, $positionsOffset: Int, $positionsWhere: positions_bool_exp, $atomsLimit: Int, $atomsOffset: Int, $atomsWhere: atoms_bool_exp, $atomsOrderBy: [atoms_order_by!], $triplesLimit: Int, $triplesOffset: Int, $triplesWhere: triples_bool_exp, $triplesOrderBy: [triples_order_by!]) {
  account(id: $address) {
    ...AccountMetadata
    ...AccountPositions
    ...AccountAtoms
    ...AccountTriples
  }
}
    ${AccountMetadataFragmentDoc}
${AccountPositionsFragmentDoc}
${AccountAtomsFragmentDoc}
${AccountTriplesFragmentDoc}`;
var useGetAccountWithPaginatedRelationsQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetAccountWithPaginatedRelations", variables],
    queryFn: fetcher(GetAccountWithPaginatedRelationsDocument, variables),
    ...options
  });
};
useGetAccountWithPaginatedRelationsQuery.document = GetAccountWithPaginatedRelationsDocument;
useGetAccountWithPaginatedRelationsQuery.getKey = (variables) => ["GetAccountWithPaginatedRelations", variables];
var useInfiniteGetAccountWithPaginatedRelationsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? [
          "GetAccountWithPaginatedRelations.infinite",
          variables
        ],
        queryFn: (metaData) => fetcher(GetAccountWithPaginatedRelationsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAccountWithPaginatedRelationsQuery.getKey = (variables) => ["GetAccountWithPaginatedRelations.infinite", variables];
useGetAccountWithPaginatedRelationsQuery.fetcher = (variables, options) => fetcher(GetAccountWithPaginatedRelationsDocument, variables, options);
var GetAccountWithAggregatesDocument = `
    query GetAccountWithAggregates($address: String!, $positionsLimit: Int, $positionsOffset: Int, $positionsWhere: positions_bool_exp, $atomsWhere: atoms_bool_exp, $atomsOrderBy: [atoms_order_by!], $atomsLimit: Int, $atomsOffset: Int, $triplesWhere: triples_bool_exp, $triplesOrderBy: [triples_order_by!], $triplesLimit: Int, $triplesOffset: Int) {
  account(id: $address) {
    ...AccountMetadata
    ...AccountPositionsAggregate
    ...AccountAtomsAggregate
    ...AccountTriplesAggregate
  }
}
    ${AccountMetadataFragmentDoc}
${AccountPositionsAggregateFragmentDoc}
${AccountAtomsAggregateFragmentDoc}
${AccountTriplesAggregateFragmentDoc}`;
var useGetAccountWithAggregatesQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetAccountWithAggregates", variables],
    queryFn: fetcher(GetAccountWithAggregatesDocument, variables),
    ...options
  });
};
useGetAccountWithAggregatesQuery.document = GetAccountWithAggregatesDocument;
useGetAccountWithAggregatesQuery.getKey = (variables) => ["GetAccountWithAggregates", variables];
var useInfiniteGetAccountWithAggregatesQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? [
          "GetAccountWithAggregates.infinite",
          variables
        ],
        queryFn: (metaData) => fetcher(GetAccountWithAggregatesDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAccountWithAggregatesQuery.getKey = (variables) => ["GetAccountWithAggregates.infinite", variables];
useGetAccountWithAggregatesQuery.fetcher = (variables, options) => fetcher(GetAccountWithAggregatesDocument, variables, options);
var GetAccountMetadataDocument = `
    query GetAccountMetadata($address: String!) {
  account(id: $address) {
    ...AccountMetadata
    atom {
      ...AtomMetadata
    }
  }
}
    ${AccountMetadataFragmentDoc}
${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}`;
var useGetAccountMetadataQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetAccountMetadata", variables],
    queryFn: fetcher(
      GetAccountMetadataDocument,
      variables
    ),
    ...options
  });
};
useGetAccountMetadataQuery.document = GetAccountMetadataDocument;
useGetAccountMetadataQuery.getKey = (variables) => ["GetAccountMetadata", variables];
var useInfiniteGetAccountMetadataQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetAccountMetadata.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetAccountMetadataDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAccountMetadataQuery.getKey = (variables) => ["GetAccountMetadata.infinite", variables];
useGetAccountMetadataQuery.fetcher = (variables, options) => fetcher(
  GetAccountMetadataDocument,
  variables,
  options
);
var GetAtomsDocument = `
    query GetAtoms($limit: Int, $offset: Int, $orderBy: [atoms_order_by!], $where: atoms_bool_exp) {
  total: atoms_aggregate(where: $where) {
    aggregate {
      count
    }
  }
  atoms(limit: $limit, offset: $offset, order_by: $orderBy, where: $where) {
    ...AtomMetadata
    ...AtomTxn
    ...AtomVaultDetails
    ...AtomTriple
    creator {
      ...AccountMetadata
    }
  }
}
    ${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AtomTxnFragmentDoc}
${AtomVaultDetailsFragmentDoc}
${AtomTripleFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetAtomsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetAtoms"] : ["GetAtoms", variables],
    queryFn: fetcher(
      GetAtomsDocument,
      variables
    ),
    ...options
  });
};
useGetAtomsQuery.document = GetAtomsDocument;
useGetAtomsQuery.getKey = (variables) => variables === void 0 ? ["GetAtoms"] : ["GetAtoms", variables];
var useInfiniteGetAtomsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetAtoms.infinite"] : ["GetAtoms.infinite", variables],
        queryFn: (metaData) => fetcher(GetAtomsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAtomsQuery.getKey = (variables) => variables === void 0 ? ["GetAtoms.infinite"] : ["GetAtoms.infinite", variables];
useGetAtomsQuery.fetcher = (variables, options) => fetcher(
  GetAtomsDocument,
  variables,
  options
);
var GetAtomsWithPositionsDocument = `
    query GetAtomsWithPositions($limit: Int, $offset: Int, $orderBy: [atoms_order_by!], $where: atoms_bool_exp, $address: String) {
  total: atoms_aggregate(where: $where) {
    aggregate {
      count
    }
  }
  atoms(limit: $limit, offset: $offset, order_by: $orderBy, where: $where) {
    ...AtomMetadata
    ...AtomTxn
    term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        position_count
        total_shares
        current_share_price
        total: positions_aggregate {
          aggregate {
            count
            sum {
              shares
            }
          }
        }
        positions(where: {account_id: {_ilike: $address}}) {
          id
          account {
            label
            id
          }
          shares
        }
      }
    }
    creator {
      ...AccountMetadata
    }
  }
}
    ${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AtomTxnFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetAtomsWithPositionsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetAtomsWithPositions"] : ["GetAtomsWithPositions", variables],
    queryFn: fetcher(GetAtomsWithPositionsDocument, variables),
    ...options
  });
};
useGetAtomsWithPositionsQuery.document = GetAtomsWithPositionsDocument;
useGetAtomsWithPositionsQuery.getKey = (variables) => variables === void 0 ? ["GetAtomsWithPositions"] : ["GetAtomsWithPositions", variables];
var useInfiniteGetAtomsWithPositionsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetAtomsWithPositions.infinite"] : ["GetAtomsWithPositions.infinite", variables],
        queryFn: (metaData) => fetcher(GetAtomsWithPositionsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAtomsWithPositionsQuery.getKey = (variables) => variables === void 0 ? ["GetAtomsWithPositions.infinite"] : ["GetAtomsWithPositions.infinite", variables];
useGetAtomsWithPositionsQuery.fetcher = (variables, options) => fetcher(
  GetAtomsWithPositionsDocument,
  variables,
  options
);
var GetAtomsWithAggregatesDocument = `
    query GetAtomsWithAggregates($limit: Int, $offset: Int, $orderBy: [atoms_order_by!], $where: atoms_bool_exp) {
  atoms_aggregate(
    limit: $limit
    offset: $offset
    order_by: $orderBy
    where: $where
  ) {
    aggregate {
      count
    }
    nodes {
      ...AtomMetadata
      ...AtomTxn
      ...AtomVaultDetails
      creator {
        ...AccountMetadata
      }
    }
  }
}
    ${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AtomTxnFragmentDoc}
${AtomVaultDetailsFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetAtomsWithAggregatesQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetAtomsWithAggregates"] : ["GetAtomsWithAggregates", variables],
    queryFn: fetcher(GetAtomsWithAggregatesDocument, variables),
    ...options
  });
};
useGetAtomsWithAggregatesQuery.document = GetAtomsWithAggregatesDocument;
useGetAtomsWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetAtomsWithAggregates"] : ["GetAtomsWithAggregates", variables];
var useInfiniteGetAtomsWithAggregatesQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetAtomsWithAggregates.infinite"] : ["GetAtomsWithAggregates.infinite", variables],
        queryFn: (metaData) => fetcher(GetAtomsWithAggregatesDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAtomsWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetAtomsWithAggregates.infinite"] : ["GetAtomsWithAggregates.infinite", variables];
useGetAtomsWithAggregatesQuery.fetcher = (variables, options) => fetcher(
  GetAtomsWithAggregatesDocument,
  variables,
  options
);
var GetAtomsCountDocument = `
    query GetAtomsCount($where: atoms_bool_exp) {
  atoms_aggregate(where: $where) {
    aggregate {
      count
    }
  }
}
    `;
var useGetAtomsCountQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetAtomsCount"] : ["GetAtomsCount", variables],
    queryFn: fetcher(
      GetAtomsCountDocument,
      variables
    ),
    ...options
  });
};
useGetAtomsCountQuery.document = GetAtomsCountDocument;
useGetAtomsCountQuery.getKey = (variables) => variables === void 0 ? ["GetAtomsCount"] : ["GetAtomsCount", variables];
var useInfiniteGetAtomsCountQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetAtomsCount.infinite"] : ["GetAtomsCount.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetAtomsCountDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAtomsCountQuery.getKey = (variables) => variables === void 0 ? ["GetAtomsCount.infinite"] : ["GetAtomsCount.infinite", variables];
useGetAtomsCountQuery.fetcher = (variables, options) => fetcher(
  GetAtomsCountDocument,
  variables,
  options
);
var GetAtomDocument = `
    query GetAtom($id: numeric!) {
  atom(term_id: $id) {
    ...AtomMetadata
    ...AtomTxn
    ...AtomVaultDetails
    creator {
      ...AccountMetadata
    }
    ...AtomTriple
  }
}
    ${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AtomTxnFragmentDoc}
${AtomVaultDetailsFragmentDoc}
${AccountMetadataFragmentDoc}
${AtomTripleFragmentDoc}`;
var useGetAtomQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetAtom", variables],
    queryFn: fetcher(
      GetAtomDocument,
      variables
    ),
    ...options
  });
};
useGetAtomQuery.document = GetAtomDocument;
useGetAtomQuery.getKey = (variables) => [
  "GetAtom",
  variables
];
var useInfiniteGetAtomQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetAtom.infinite", variables],
        queryFn: (metaData) => fetcher(GetAtomDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAtomQuery.getKey = (variables) => [
  "GetAtom.infinite",
  variables
];
useGetAtomQuery.fetcher = (variables, options) => fetcher(
  GetAtomDocument,
  variables,
  options
);
var GetAtomByDataDocument = `
    query GetAtomByData($data: String!) {
  atoms(where: {data: {_eq: $data}}) {
    ...AtomMetadata
    ...AtomTxn
    ...AtomVaultDetails
    creator {
      ...AccountMetadata
    }
    ...AtomTriple
  }
}
    ${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AtomTxnFragmentDoc}
${AtomVaultDetailsFragmentDoc}
${AccountMetadataFragmentDoc}
${AtomTripleFragmentDoc}`;
var useGetAtomByDataQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetAtomByData", variables],
    queryFn: fetcher(
      GetAtomByDataDocument,
      variables
    ),
    ...options
  });
};
useGetAtomByDataQuery.document = GetAtomByDataDocument;
useGetAtomByDataQuery.getKey = (variables) => [
  "GetAtomByData",
  variables
];
var useInfiniteGetAtomByDataQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetAtomByData.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetAtomByDataDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAtomByDataQuery.getKey = (variables) => ["GetAtomByData.infinite", variables];
useGetAtomByDataQuery.fetcher = (variables, options) => fetcher(
  GetAtomByDataDocument,
  variables,
  options
);
var GetVerifiedAtomDetailsDocument = `
    query GetVerifiedAtomDetails($id: numeric!, $userPositionAddress: String!) {
  atom(term_id: $id) {
    term_id
    label
    wallet_id
    image
    type
    created_at
    data
    creator {
      id
    }
    value {
      thing {
        name
        description
        url
      }
    }
    term {
      vaults(where: {curve_id: {_eq: "1"}, term_id: {_eq: $id}}) {
        current_share_price
        total_shares
        position_count
        userPosition: positions(
          limit: 1
          where: {account_id: {_ilike: $userPositionAddress}}
        ) {
          shares
          account_id
        }
      }
    }
    tags: as_subject_triples_aggregate(where: {predicate_id: {_in: [3]}}) {
      nodes {
        object {
          label
          term {
            vaults(where: {curve_id: {_eq: "1"}, term_id: {_eq: $id}}) {
              term_id
            }
          }
        }
        predicate_id
      }
      aggregate {
        count
      }
    }
    verificationTriple: as_subject_triples_aggregate(
      where: {predicate_id: {_eq: "4"}, object_id: {_eq: "126451"}}
    ) {
      nodes {
        term_id
        predicate_id
        object_id
        term {
          vaults(where: {curve_id: {_eq: "1"}}) {
            term_id
            positions(
              where: {account_id: {_in: ["0xD99811847E634d33f0DAcE483c52949bEc76300F", "0xBb285b543C96C927FC320Fb28524899C2C90806C", "0x0b162525C5dc8c18F771E60fD296913030Bfe42c", "0xbd2DE08aF9470c87C4475117Fb912B8f1d588D9c", "0xB95ca3D3144e9d1DAFF0EE3d35a4488A4A5C9Fc5"]}}
            ) {
              id
              shares
              account_id
              account {
                id
              }
            }
          }
        }
      }
    }
  }
}
    `;
var useGetVerifiedAtomDetailsQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetVerifiedAtomDetails", variables],
    queryFn: fetcher(GetVerifiedAtomDetailsDocument, variables),
    ...options
  });
};
useGetVerifiedAtomDetailsQuery.document = GetVerifiedAtomDetailsDocument;
useGetVerifiedAtomDetailsQuery.getKey = (variables) => ["GetVerifiedAtomDetails", variables];
var useInfiniteGetVerifiedAtomDetailsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? [
          "GetVerifiedAtomDetails.infinite",
          variables
        ],
        queryFn: (metaData) => fetcher(GetVerifiedAtomDetailsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetVerifiedAtomDetailsQuery.getKey = (variables) => ["GetVerifiedAtomDetails.infinite", variables];
useGetVerifiedAtomDetailsQuery.fetcher = (variables, options) => fetcher(
  GetVerifiedAtomDetailsDocument,
  variables,
  options
);
var GetAtomDetailsDocument = `
    query GetAtomDetails($id: numeric!, $userPositionAddress: String!) {
  atom(term_id: $id) {
    term_id
    label
    wallet_id
    image
    type
    created_at
    data
    creator {
      id
    }
    value {
      thing {
        name
        description
        url
      }
    }
    term {
      vaults(where: {curve_id: {_eq: "1"}, term_id: {_eq: $id}}) {
        current_share_price
        total_shares
        position_count
        userPosition: positions(
          limit: 1
          where: {account_id: {_eq: $userPositionAddress}}
        ) {
          shares
          account_id
        }
      }
    }
    tags: as_subject_triples_aggregate(where: {predicate_id: {_in: [3]}}) {
      nodes {
        object {
          label
          term {
            vaults(where: {curve_id: {_eq: "1"}, term_id: {_eq: $id}}) {
              term_id
            }
          }
        }
        predicate_id
      }
      aggregate {
        count
      }
    }
  }
}
    `;
var useGetAtomDetailsQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetAtomDetails", variables],
    queryFn: fetcher(
      GetAtomDetailsDocument,
      variables
    ),
    ...options
  });
};
useGetAtomDetailsQuery.document = GetAtomDetailsDocument;
useGetAtomDetailsQuery.getKey = (variables) => [
  "GetAtomDetails",
  variables
];
var useInfiniteGetAtomDetailsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetAtomDetails.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetAtomDetailsDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAtomDetailsQuery.getKey = (variables) => ["GetAtomDetails.infinite", variables];
useGetAtomDetailsQuery.fetcher = (variables, options) => fetcher(
  GetAtomDetailsDocument,
  variables,
  options
);
var GetEventsDocument = `
    query GetEvents($limit: Int, $offset: Int, $orderBy: [events_order_by!], $where: events_bool_exp, $addresses: [String!]) {
  total: events_aggregate(where: $where) {
    aggregate {
      count
    }
  }
  events(limit: $limit, offset: $offset, order_by: $orderBy, where: $where) {
    id
    block_number
    created_at
    type
    transaction_hash
    atom_id
    triple_id
    deposit_id
    redemption_id
    atom {
      ...AtomMetadata
      term {
        vaults(where: {curve_id: {_eq: "1"}}) {
          total_shares
          position_count
          positions(where: {account: {id: {_in: $addresses}}}) {
            account_id
            shares
            account {
              id
              label
              image
            }
          }
        }
      }
    }
    triple {
      term_id
      creator {
        ...AccountMetadata
      }
      subject {
        data
        term_id
        image
        label
        emoji
        type
        ...AtomValue
        creator {
          ...AccountMetadata
        }
      }
      predicate {
        data
        term_id
        image
        label
        emoji
        type
        ...AtomValue
        creator {
          ...AccountMetadata
        }
      }
      object {
        data
        term_id
        image
        label
        emoji
        type
        ...AtomValue
        creator {
          ...AccountMetadata
        }
      }
      term {
        vaults(where: {curve_id: {_eq: "1"}}) {
          total_shares
          position_count
          positions(where: {account: {id: {_in: $addresses}}}) {
            account_id
            shares
            account {
              id
              label
              image
            }
          }
        }
      }
      counter_term {
        vaults(where: {curve_id: {_eq: "1"}}) {
          total_shares
          position_count
          positions(where: {account: {id: {_in: $addresses}}}) {
            account_id
            shares
            account {
              id
              label
              image
            }
          }
        }
      }
    }
    deposit {
      sender_id
      sender {
        id
      }
      shares_for_receiver
      sender_assets_after_total_fees
      vault {
        total_shares
        position_count
        positions(where: {account: {id: {_in: $addresses}}}) {
          account_id
          shares
          account {
            id
            label
            image
          }
        }
      }
    }
    redemption {
      sender_id
      sender {
        id
      }
    }
  }
}
    ${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetEventsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetEvents"] : ["GetEvents", variables],
    queryFn: fetcher(
      GetEventsDocument,
      variables
    ),
    ...options
  });
};
useGetEventsQuery.document = GetEventsDocument;
useGetEventsQuery.getKey = (variables) => variables === void 0 ? ["GetEvents"] : ["GetEvents", variables];
var useInfiniteGetEventsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetEvents.infinite"] : ["GetEvents.infinite", variables],
        queryFn: (metaData) => fetcher(GetEventsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetEventsQuery.getKey = (variables) => variables === void 0 ? ["GetEvents.infinite"] : ["GetEvents.infinite", variables];
useGetEventsQuery.fetcher = (variables, options) => fetcher(
  GetEventsDocument,
  variables,
  options
);
var GetEventsWithAggregatesDocument = `
    query GetEventsWithAggregates($limit: Int, $offset: Int, $orderBy: [events_order_by!], $where: events_bool_exp, $addresses: [String!]) {
  events_aggregate(
    where: $where
    limit: $limit
    offset: $offset
    order_by: $orderBy
  ) {
    aggregate {
      count
      max {
        created_at
        block_number
      }
      min {
        created_at
        block_number
      }
    }
    nodes {
      ...EventDetails
    }
  }
}
    ${EventDetailsFragmentDoc}
${DepositEventFragmentFragmentDoc}
${RedemptionEventFragmentFragmentDoc}
${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}
${TripleMetadataFragmentDoc}
${AccountMetadataFragmentDoc}
${PositionAggregateFieldsFragmentDoc}
${PositionFieldsFragmentDoc}`;
var useGetEventsWithAggregatesQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetEventsWithAggregates"] : ["GetEventsWithAggregates", variables],
    queryFn: fetcher(GetEventsWithAggregatesDocument, variables),
    ...options
  });
};
useGetEventsWithAggregatesQuery.document = GetEventsWithAggregatesDocument;
useGetEventsWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetEventsWithAggregates"] : ["GetEventsWithAggregates", variables];
var useInfiniteGetEventsWithAggregatesQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetEventsWithAggregates.infinite"] : ["GetEventsWithAggregates.infinite", variables],
        queryFn: (metaData) => fetcher(GetEventsWithAggregatesDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetEventsWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetEventsWithAggregates.infinite"] : ["GetEventsWithAggregates.infinite", variables];
useGetEventsWithAggregatesQuery.fetcher = (variables, options) => fetcher(
  GetEventsWithAggregatesDocument,
  variables,
  options
);
var GetEventsCountDocument = `
    query GetEventsCount($where: events_bool_exp) {
  events_aggregate(where: $where) {
    aggregate {
      count
    }
  }
}
    `;
var useGetEventsCountQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetEventsCount"] : ["GetEventsCount", variables],
    queryFn: fetcher(
      GetEventsCountDocument,
      variables
    ),
    ...options
  });
};
useGetEventsCountQuery.document = GetEventsCountDocument;
useGetEventsCountQuery.getKey = (variables) => variables === void 0 ? ["GetEventsCount"] : ["GetEventsCount", variables];
var useInfiniteGetEventsCountQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetEventsCount.infinite"] : ["GetEventsCount.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetEventsCountDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetEventsCountQuery.getKey = (variables) => variables === void 0 ? ["GetEventsCount.infinite"] : ["GetEventsCount.infinite", variables];
useGetEventsCountQuery.fetcher = (variables, options) => fetcher(
  GetEventsCountDocument,
  variables,
  options
);
var GetEventsDataDocument = `
    query GetEventsData($where: events_bool_exp) {
  events_aggregate(where: $where) {
    aggregate {
      count
      max {
        created_at
        block_number
      }
      min {
        created_at
        block_number
      }
      avg {
        block_number
      }
    }
  }
}
    `;
var useGetEventsDataQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetEventsData"] : ["GetEventsData", variables],
    queryFn: fetcher(
      GetEventsDataDocument,
      variables
    ),
    ...options
  });
};
useGetEventsDataQuery.document = GetEventsDataDocument;
useGetEventsDataQuery.getKey = (variables) => variables === void 0 ? ["GetEventsData"] : ["GetEventsData", variables];
var useInfiniteGetEventsDataQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetEventsData.infinite"] : ["GetEventsData.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetEventsDataDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetEventsDataQuery.getKey = (variables) => variables === void 0 ? ["GetEventsData.infinite"] : ["GetEventsData.infinite", variables];
useGetEventsDataQuery.fetcher = (variables, options) => fetcher(
  GetEventsDataDocument,
  variables,
  options
);
var GetDebugEventsDocument = `
    query GetDebugEvents($addresses: [String!]) {
  debug_events: events {
    id
    atom {
      term {
        positions(where: {account_id: {_in: $addresses}}) {
          account_id
          shares
        }
      }
    }
  }
}
    `;
var useGetDebugEventsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetDebugEvents"] : ["GetDebugEvents", variables],
    queryFn: fetcher(
      GetDebugEventsDocument,
      variables
    ),
    ...options
  });
};
useGetDebugEventsQuery.document = GetDebugEventsDocument;
useGetDebugEventsQuery.getKey = (variables) => variables === void 0 ? ["GetDebugEvents"] : ["GetDebugEvents", variables];
var useInfiniteGetDebugEventsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetDebugEvents.infinite"] : ["GetDebugEvents.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetDebugEventsDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetDebugEventsQuery.getKey = (variables) => variables === void 0 ? ["GetDebugEvents.infinite"] : ["GetDebugEvents.infinite", variables];
useGetDebugEventsQuery.fetcher = (variables, options) => fetcher(
  GetDebugEventsDocument,
  variables,
  options
);
var GetFollowingPositionsDocument = `
    query GetFollowingPositions($subjectId: numeric!, $predicateId: numeric!, $address: String!, $limit: Int, $offset: Int, $positionsOrderBy: [positions_order_by!]) {
  triples_aggregate(
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}, {positions: {account_id: {_ilike: $address}}}]}
  ) {
    aggregate {
      count
    }
  }
  triples(
    limit: $limit
    offset: $offset
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}, {positions: {account_id: {_ilike: $address}}}]}
  ) {
    term_id
    subject {
      ...AtomMetadata
    }
    predicate {
      ...AtomMetadata
    }
    object {
      ...AtomMetadata
    }
    term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        total_shares
        current_share_price
        positions_aggregate {
          aggregate {
            count
            sum {
              shares
            }
          }
        }
        positions(where: {account_id: {_ilike: $address}}, order_by: $positionsOrderBy) {
          account_id
          account {
            id
            label
          }
          shares
        }
      }
    }
  }
}
    ${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}`;
var useGetFollowingPositionsQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetFollowingPositions", variables],
    queryFn: fetcher(GetFollowingPositionsDocument, variables),
    ...options
  });
};
useGetFollowingPositionsQuery.document = GetFollowingPositionsDocument;
useGetFollowingPositionsQuery.getKey = (variables) => ["GetFollowingPositions", variables];
var useInfiniteGetFollowingPositionsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? [
          "GetFollowingPositions.infinite",
          variables
        ],
        queryFn: (metaData) => fetcher(GetFollowingPositionsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetFollowingPositionsQuery.getKey = (variables) => ["GetFollowingPositions.infinite", variables];
useGetFollowingPositionsQuery.fetcher = (variables, options) => fetcher(
  GetFollowingPositionsDocument,
  variables,
  options
);
var GetFollowerPositionsDocument = `
    query GetFollowerPositions($subjectId: numeric!, $predicateId: numeric!, $objectId: numeric!, $positionsLimit: Int, $positionsOffset: Int, $positionsOrderBy: [positions_order_by!], $positionsWhere: positions_bool_exp) {
  triples(
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}, {object_id: {_eq: $objectId}}]}
  ) {
    term_id
    subject {
      ...AtomMetadata
    }
    predicate {
      ...AtomMetadata
    }
    object {
      ...AtomMetadata
    }
    term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        total_shares
        current_share_price
        positions_aggregate {
          aggregate {
            count
            sum {
              shares
            }
          }
        }
        positions(
          limit: $positionsLimit
          offset: $positionsOffset
          order_by: $positionsOrderBy
          where: $positionsWhere
        ) {
          account {
            id
            label
            image
          }
          shares
        }
      }
    }
  }
}
    ${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}`;
var useGetFollowerPositionsQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetFollowerPositions", variables],
    queryFn: fetcher(GetFollowerPositionsDocument, variables),
    ...options
  });
};
useGetFollowerPositionsQuery.document = GetFollowerPositionsDocument;
useGetFollowerPositionsQuery.getKey = (variables) => ["GetFollowerPositions", variables];
var useInfiniteGetFollowerPositionsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? [
          "GetFollowerPositions.infinite",
          variables
        ],
        queryFn: (metaData) => fetcher(GetFollowerPositionsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetFollowerPositionsQuery.getKey = (variables) => ["GetFollowerPositions.infinite", variables];
useGetFollowerPositionsQuery.fetcher = (variables, options) => fetcher(
  GetFollowerPositionsDocument,
  variables,
  options
);
var GetConnectionsDocument = `
    query GetConnections($subjectId: numeric!, $predicateId: numeric!, $objectId: numeric!, $addresses: [String!], $positionsLimit: Int, $positionsOffset: Int, $positionsOrderBy: [positions_order_by!], $positionsWhere: positions_bool_exp) {
  following_count: triples_aggregate(
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}, {object_id: {_eq: $objectId}}]}
  ) {
    aggregate {
      count
    }
  }
  following: triples(
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}, {object_id: {_eq: $objectId}}]}
  ) {
    ...FollowMetadata
  }
  followers_count: triples_aggregate(
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}, {positions: {account_id: {_in: $addresses}}}]}
  ) {
    aggregate {
      count
    }
  }
  followers: triples(
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}, {positions: {account_id: {_in: $addresses}}}]}
  ) {
    ...FollowMetadata
  }
}
    ${FollowMetadataFragmentDoc}`;
var useGetConnectionsQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetConnections", variables],
    queryFn: fetcher(
      GetConnectionsDocument,
      variables
    ),
    ...options
  });
};
useGetConnectionsQuery.document = GetConnectionsDocument;
useGetConnectionsQuery.getKey = (variables) => [
  "GetConnections",
  variables
];
var useInfiniteGetConnectionsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetConnections.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetConnectionsDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetConnectionsQuery.getKey = (variables) => ["GetConnections.infinite", variables];
useGetConnectionsQuery.fetcher = (variables, options) => fetcher(
  GetConnectionsDocument,
  variables,
  options
);
var GetConnectionsCountDocument = `
    query GetConnectionsCount($subjectId: numeric!, $predicateId: numeric!, $objectId: numeric!, $address: String!) {
  following_count: triples_aggregate(
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}, {positions: {account_id: {_ilike: $address}}}]}
  ) {
    aggregate {
      count
    }
  }
  followers_count: triples(
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}, {object_id: {_eq: $objectId}}]}
  ) {
    term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        positions_aggregate {
          aggregate {
            count
            sum {
              shares
            }
          }
        }
      }
    }
  }
}
    `;
var useGetConnectionsCountQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetConnectionsCount", variables],
    queryFn: fetcher(GetConnectionsCountDocument, variables),
    ...options
  });
};
useGetConnectionsCountQuery.document = GetConnectionsCountDocument;
useGetConnectionsCountQuery.getKey = (variables) => ["GetConnectionsCount", variables];
var useInfiniteGetConnectionsCountQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? [
          "GetConnectionsCount.infinite",
          variables
        ],
        queryFn: (metaData) => fetcher(
          GetConnectionsCountDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetConnectionsCountQuery.getKey = (variables) => ["GetConnectionsCount.infinite", variables];
useGetConnectionsCountQuery.fetcher = (variables, options) => fetcher(
  GetConnectionsCountDocument,
  variables,
  options
);
var GetListsDocument = `
    query GetLists($where: predicate_objects_bool_exp) {
  predicate_objects_aggregate(where: $where) {
    aggregate {
      count
    }
  }
  predicate_objects(where: $where, order_by: [{triple_count: desc}]) {
    id
    triple_count
    object {
      term_id
      label
      image
    }
  }
}
    `;
var useGetListsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetLists"] : ["GetLists", variables],
    queryFn: fetcher(
      GetListsDocument,
      variables
    ),
    ...options
  });
};
useGetListsQuery.document = GetListsDocument;
useGetListsQuery.getKey = (variables) => variables === void 0 ? ["GetLists"] : ["GetLists", variables];
var useInfiniteGetListsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetLists.infinite"] : ["GetLists.infinite", variables],
        queryFn: (metaData) => fetcher(GetListsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetListsQuery.getKey = (variables) => variables === void 0 ? ["GetLists.infinite"] : ["GetLists.infinite", variables];
useGetListsQuery.fetcher = (variables, options) => fetcher(
  GetListsDocument,
  variables,
  options
);
var GetListItemsDocument = `
    query GetListItems($predicateId: numeric, $objectId: numeric) {
  triples_aggregate(
    where: {predicate_id: {_eq: predicateId}, object_id: {_eq: $objectId}}
    order_by: [{term: {positions_aggregate: {count: desc}}, counter_term: {positions_aggregate: {count: desc}}}]
  ) {
    aggregate {
      count
    }
    nodes {
      ...TripleVaultDetails
    }
  }
}
    ${TripleVaultDetailsFragmentDoc}
${PositionDetailsFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetListItemsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetListItems"] : ["GetListItems", variables],
    queryFn: fetcher(
      GetListItemsDocument,
      variables
    ),
    ...options
  });
};
useGetListItemsQuery.document = GetListItemsDocument;
useGetListItemsQuery.getKey = (variables) => variables === void 0 ? ["GetListItems"] : ["GetListItems", variables];
var useInfiniteGetListItemsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetListItems.infinite"] : ["GetListItems.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetListItemsDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetListItemsQuery.getKey = (variables) => variables === void 0 ? ["GetListItems.infinite"] : ["GetListItems.infinite", variables];
useGetListItemsQuery.fetcher = (variables, options) => fetcher(
  GetListItemsDocument,
  variables,
  options
);
var GetListDetailsDocument = `
    query GetListDetails($globalWhere: triples_bool_exp, $tagPredicateId: numeric, $limit: Int, $offset: Int, $orderBy: [triples_order_by!]) {
  globalTriplesAggregate: triples_aggregate(where: $globalWhere) {
    aggregate {
      count
    }
  }
  globalTriples: triples(
    where: $globalWhere
    limit: $limit
    offset: $offset
    order_by: $orderBy
  ) {
    term_id
    counter_term_id
    subject {
      term_id
      label
      wallet_id
      image
      type
      term {
        vaults {
          position_count
        }
      }
      tags: as_subject_triples_aggregate(
        where: {predicate_id: {_eq: $tagPredicateId}}
      ) {
        nodes {
          object {
            label
            term_id
            taggedIdentities: as_object_triples_aggregate {
              nodes {
                subject {
                  label
                  term_id
                }
                term_id
              }
              aggregate {
                count
              }
            }
          }
        }
        aggregate {
          count
        }
      }
    }
    object {
      term_id
      label
      wallet_id
      image
      type
    }
    predicate {
      term_id
      label
      wallet_id
      image
      type
    }
    term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        current_share_price
        position_count
        total_shares
      }
    }
    counter_term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        current_share_price
        position_count
        total_shares
      }
    }
  }
}
    `;
var useGetListDetailsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetListDetails"] : ["GetListDetails", variables],
    queryFn: fetcher(
      GetListDetailsDocument,
      variables
    ),
    ...options
  });
};
useGetListDetailsQuery.document = GetListDetailsDocument;
useGetListDetailsQuery.getKey = (variables) => variables === void 0 ? ["GetListDetails"] : ["GetListDetails", variables];
var useInfiniteGetListDetailsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetListDetails.infinite"] : ["GetListDetails.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetListDetailsDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetListDetailsQuery.getKey = (variables) => variables === void 0 ? ["GetListDetails.infinite"] : ["GetListDetails.infinite", variables];
useGetListDetailsQuery.fetcher = (variables, options) => fetcher(
  GetListDetailsDocument,
  variables,
  options
);
var GetListDetailsWithPositionDocument = `
    query GetListDetailsWithPosition($globalWhere: triples_bool_exp, $tagPredicateId: numeric, $address: String, $limit: Int, $offset: Int, $orderBy: [triples_order_by!]) {
  globalTriplesAggregate: triples_aggregate(where: $globalWhere) {
    aggregate {
      count
    }
  }
  globalTriples: triples(
    where: $globalWhere
    limit: $limit
    offset: $offset
    order_by: $orderBy
  ) {
    term_id
    counter_term_id
    subject {
      term_id
      label
      wallet_id
      image
      type
      tags: as_subject_triples_aggregate(
        where: {predicate_id: {_eq: $tagPredicateId}}
      ) {
        nodes {
          object {
            label
            term_id
            taggedIdentities: as_object_triples_aggregate {
              nodes {
                subject {
                  label
                  term_id
                }
                term_id
              }
              aggregate {
                count
              }
            }
          }
        }
        aggregate {
          count
        }
      }
    }
    object {
      term_id
      label
      wallet_id
      image
      type
    }
    predicate {
      term_id
      label
      wallet_id
      image
      type
    }
    term {
      vaults {
        current_share_price
        position_count
        total_shares
      }
      positions(where: {account_id: {_ilike: $address}}) {
        account {
          id
          label
          image
        }
        shares
      }
    }
    counter_term {
      vaults {
        current_share_price
        position_count
        total_shares
      }
      positions(where: {account_id: {_ilike: $address}}) {
        account {
          id
          label
          image
        }
        shares
      }
    }
  }
}
    `;
var useGetListDetailsWithPositionQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetListDetailsWithPosition"] : ["GetListDetailsWithPosition", variables],
    queryFn: fetcher(GetListDetailsWithPositionDocument, variables),
    ...options
  });
};
useGetListDetailsWithPositionQuery.document = GetListDetailsWithPositionDocument;
useGetListDetailsWithPositionQuery.getKey = (variables) => variables === void 0 ? ["GetListDetailsWithPosition"] : ["GetListDetailsWithPosition", variables];
var useInfiniteGetListDetailsWithPositionQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetListDetailsWithPosition.infinite"] : ["GetListDetailsWithPosition.infinite", variables],
        queryFn: (metaData) => fetcher(GetListDetailsWithPositionDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetListDetailsWithPositionQuery.getKey = (variables) => variables === void 0 ? ["GetListDetailsWithPosition.infinite"] : ["GetListDetailsWithPosition.infinite", variables];
useGetListDetailsWithPositionQuery.fetcher = (variables, options) => fetcher(GetListDetailsWithPositionDocument, variables, options);
var GetListDetailsWithUserDocument = `
    query GetListDetailsWithUser($globalWhere: triples_bool_exp, $userWhere: triples_bool_exp, $tagPredicateId: numeric, $address: String, $limit: Int, $offset: Int, $orderBy: [triples_order_by!]) {
  globalTriplesAggregate: triples_aggregate(where: $globalWhere) {
    aggregate {
      count
    }
  }
  globalTriples: triples(
    where: $globalWhere
    limit: $limit
    offset: $offset
    order_by: $orderBy
  ) {
    term_id
    counter_term_id
    subject {
      term_id
      label
      wallet_id
      image
      type
      tags: as_subject_triples_aggregate(
        where: {predicate_id: {_eq: $tagPredicateId}}
      ) {
        nodes {
          object {
            label
            term_id
            taggedIdentities: as_object_triples_aggregate {
              nodes {
                subject {
                  label
                  term_id
                }
                term_id
              }
              aggregate {
                count
              }
            }
          }
        }
        aggregate {
          count
        }
      }
    }
    object {
      term_id
      label
      wallet_id
      image
      type
    }
    predicate {
      term_id
      label
      wallet_id
      image
      type
    }
    term {
      vaults {
        current_share_price
        position_count
        total_shares
      }
      positions(where: {account_id: {_ilike: $address}}) {
        account {
          id
          label
          image
        }
        shares
      }
    }
    counter_term {
      vaults {
        current_share_price
        position_count
        total_shares
      }
      positions(where: {account_id: {_ilike: $address}}) {
        account {
          id
          label
          image
        }
        shares
      }
    }
  }
  userTriplesAggregate: triples_aggregate(where: $userWhere) {
    aggregate {
      count
    }
  }
  userTriples: triples(where: $userWhere) {
    term_id
    counter_term_id
    subject {
      term_id
      label
      wallet_id
      image
      type
      tags: as_subject_triples_aggregate(
        where: {predicate_id: {_eq: $tagPredicateId}}
      ) {
        nodes {
          object {
            label
            term_id
            taggedIdentities: as_object_triples_aggregate {
              nodes {
                subject {
                  label
                  term_id
                }
                term_id
              }
              aggregate {
                count
              }
            }
          }
        }
        aggregate {
          count
        }
      }
    }
    object {
      term_id
      label
      wallet_id
      image
      type
    }
    predicate {
      term_id
      label
      wallet_id
      image
      type
    }
    term {
      vaults {
        current_share_price
        position_count
        total_shares
      }
      positions(where: {account_id: {_ilike: $address}}) {
        account {
          id
          label
          image
        }
        shares
      }
    }
    counter_term {
      vaults {
        current_share_price
        position_count
        total_shares
      }
      positions(where: {account_id: {_ilike: $address}}) {
        account {
          id
          label
          image
        }
        shares
      }
    }
  }
}
    `;
var useGetListDetailsWithUserQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetListDetailsWithUser"] : ["GetListDetailsWithUser", variables],
    queryFn: fetcher(GetListDetailsWithUserDocument, variables),
    ...options
  });
};
useGetListDetailsWithUserQuery.document = GetListDetailsWithUserDocument;
useGetListDetailsWithUserQuery.getKey = (variables) => variables === void 0 ? ["GetListDetailsWithUser"] : ["GetListDetailsWithUser", variables];
var useInfiniteGetListDetailsWithUserQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetListDetailsWithUser.infinite"] : ["GetListDetailsWithUser.infinite", variables],
        queryFn: (metaData) => fetcher(GetListDetailsWithUserDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetListDetailsWithUserQuery.getKey = (variables) => variables === void 0 ? ["GetListDetailsWithUser.infinite"] : ["GetListDetailsWithUser.infinite", variables];
useGetListDetailsWithUserQuery.fetcher = (variables, options) => fetcher(
  GetListDetailsWithUserDocument,
  variables,
  options
);
var GetListDetailsSimplifiedDocument = `
    query GetListDetailsSimplified($globalWhere: triples_bool_exp, $address: String, $limit: Int, $offset: Int, $orderBy: [triples_order_by!]) {
  globalTriplesAggregate: triples_aggregate(where: $globalWhere) {
    aggregate {
      count
    }
  }
  globalTriples: triples(
    where: $globalWhere
    limit: $limit
    offset: $offset
    order_by: $orderBy
  ) {
    term_id
    counter_term_id
    subject {
      term_id
      label
      wallet_id
      image
      type
      term {
        vaults(where: {curve_id: {_eq: "1"}}) {
          current_share_price
          position_count
          total_shares
        }
      }
    }
    object {
      term_id
      label
      wallet_id
      image
      type
    }
    predicate {
      term_id
      label
      wallet_id
      image
      type
    }
    term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        current_share_price
        position_count
        total_shares
        positions(where: {account_id: {_ilike: $address}}) {
          account {
            id
            label
            image
          }
          shares
        }
      }
    }
    counter_term {
      vaults(where: {curve_id: {_eq: "1"}}) {
        current_share_price
        position_count
        total_shares
        positions(where: {account_id: {_ilike: $address}}) {
          account {
            id
            label
            image
          }
          shares
        }
      }
    }
  }
}
    `;
var useGetListDetailsSimplifiedQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetListDetailsSimplified"] : ["GetListDetailsSimplified", variables],
    queryFn: fetcher(GetListDetailsSimplifiedDocument, variables),
    ...options
  });
};
useGetListDetailsSimplifiedQuery.document = GetListDetailsSimplifiedDocument;
useGetListDetailsSimplifiedQuery.getKey = (variables) => variables === void 0 ? ["GetListDetailsSimplified"] : ["GetListDetailsSimplified", variables];
var useInfiniteGetListDetailsSimplifiedQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetListDetailsSimplified.infinite"] : ["GetListDetailsSimplified.infinite", variables],
        queryFn: (metaData) => fetcher(GetListDetailsSimplifiedDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetListDetailsSimplifiedQuery.getKey = (variables) => variables === void 0 ? ["GetListDetailsSimplified.infinite"] : ["GetListDetailsSimplified.infinite", variables];
useGetListDetailsSimplifiedQuery.fetcher = (variables, options) => fetcher(GetListDetailsSimplifiedDocument, variables, options);
var GetFeeTransfersDocument = `
    query GetFeeTransfers($address: String!, $cutoff_timestamp: timestamptz) {
  before_cutoff: fee_transfers_aggregate(
    where: {created_at: {_lte: $cutoff_timestamp}, sender_id: {_ilike: $address}}
  ) {
    aggregate {
      sum {
        amount
      }
    }
  }
  after_cutoff: fee_transfers_aggregate(
    where: {created_at: {_gt: $cutoff_timestamp}, sender_id: {_ilike: $address}}
  ) {
    aggregate {
      sum {
        amount
      }
    }
  }
}
    `;
var useGetFeeTransfersQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetFeeTransfers", variables],
    queryFn: fetcher(
      GetFeeTransfersDocument,
      variables
    ),
    ...options
  });
};
useGetFeeTransfersQuery.document = GetFeeTransfersDocument;
useGetFeeTransfersQuery.getKey = (variables) => [
  "GetFeeTransfers",
  variables
];
var useInfiniteGetFeeTransfersQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetFeeTransfers.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetFeeTransfersDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetFeeTransfersQuery.getKey = (variables) => ["GetFeeTransfers.infinite", variables];
useGetFeeTransfersQuery.fetcher = (variables, options) => fetcher(
  GetFeeTransfersDocument,
  variables,
  options
);
var GetPositionsDocument = `
    query GetPositions($limit: Int, $offset: Int, $orderBy: [positions_order_by!], $where: positions_bool_exp) {
  total: positions_aggregate(where: $where) {
    aggregate {
      count
      sum {
        shares
      }
    }
  }
  positions(limit: $limit, offset: $offset, order_by: $orderBy, where: $where) {
    ...PositionDetails
  }
}
    ${PositionDetailsFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetPositionsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetPositions"] : ["GetPositions", variables],
    queryFn: fetcher(
      GetPositionsDocument,
      variables
    ),
    ...options
  });
};
useGetPositionsQuery.document = GetPositionsDocument;
useGetPositionsQuery.getKey = (variables) => variables === void 0 ? ["GetPositions"] : ["GetPositions", variables];
var useInfiniteGetPositionsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetPositions.infinite"] : ["GetPositions.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetPositionsDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetPositionsQuery.getKey = (variables) => variables === void 0 ? ["GetPositions.infinite"] : ["GetPositions.infinite", variables];
useGetPositionsQuery.fetcher = (variables, options) => fetcher(
  GetPositionsDocument,
  variables,
  options
);
var GetTriplePositionsByAddressDocument = `
    query GetTriplePositionsByAddress($limit: Int, $offset: Int, $orderBy: [positions_order_by!], $where: positions_bool_exp, $address: String!) {
  total: positions_aggregate(where: $where) {
    aggregate {
      count
      sum {
        shares
      }
    }
  }
  positions(limit: $limit, offset: $offset, order_by: $orderBy, where: $where) {
    ...PositionDetails
    vault {
      term_id
      term {
        triple {
          term {
            positions(where: {account_id: {_ilike: $address}}) {
              account {
                id
                label
                image
              }
              shares
            }
          }
          counter_term {
            positions(where: {account_id: {_ilike: $address}}) {
              account {
                id
                label
                image
              }
              shares
            }
          }
        }
      }
    }
  }
}
    ${PositionDetailsFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetTriplePositionsByAddressQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetTriplePositionsByAddress", variables],
    queryFn: fetcher(GetTriplePositionsByAddressDocument, variables),
    ...options
  });
};
useGetTriplePositionsByAddressQuery.document = GetTriplePositionsByAddressDocument;
useGetTriplePositionsByAddressQuery.getKey = (variables) => ["GetTriplePositionsByAddress", variables];
var useInfiniteGetTriplePositionsByAddressQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? [
          "GetTriplePositionsByAddress.infinite",
          variables
        ],
        queryFn: (metaData) => fetcher(GetTriplePositionsByAddressDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetTriplePositionsByAddressQuery.getKey = (variables) => ["GetTriplePositionsByAddress.infinite", variables];
useGetTriplePositionsByAddressQuery.fetcher = (variables, options) => fetcher(GetTriplePositionsByAddressDocument, variables, options);
var GetPositionsWithAggregatesDocument = `
    query GetPositionsWithAggregates($limit: Int, $offset: Int, $orderBy: [positions_order_by!], $where: positions_bool_exp) {
  positions_aggregate(
    limit: $limit
    offset: $offset
    order_by: $orderBy
    where: $where
  ) {
    aggregate {
      count
    }
    nodes {
      ...PositionDetails
    }
  }
}
    ${PositionDetailsFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetPositionsWithAggregatesQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetPositionsWithAggregates"] : ["GetPositionsWithAggregates", variables],
    queryFn: fetcher(GetPositionsWithAggregatesDocument, variables),
    ...options
  });
};
useGetPositionsWithAggregatesQuery.document = GetPositionsWithAggregatesDocument;
useGetPositionsWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetPositionsWithAggregates"] : ["GetPositionsWithAggregates", variables];
var useInfiniteGetPositionsWithAggregatesQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetPositionsWithAggregates.infinite"] : ["GetPositionsWithAggregates.infinite", variables],
        queryFn: (metaData) => fetcher(GetPositionsWithAggregatesDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetPositionsWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetPositionsWithAggregates.infinite"] : ["GetPositionsWithAggregates.infinite", variables];
useGetPositionsWithAggregatesQuery.fetcher = (variables, options) => fetcher(GetPositionsWithAggregatesDocument, variables, options);
var GetPositionsCountDocument = `
    query GetPositionsCount($where: positions_bool_exp) {
  positions_aggregate(where: $where) {
    total: aggregate {
      count
      sum {
        shares
      }
    }
  }
}
    `;
var useGetPositionsCountQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetPositionsCount"] : ["GetPositionsCount", variables],
    queryFn: fetcher(
      GetPositionsCountDocument,
      variables
    ),
    ...options
  });
};
useGetPositionsCountQuery.document = GetPositionsCountDocument;
useGetPositionsCountQuery.getKey = (variables) => variables === void 0 ? ["GetPositionsCount"] : ["GetPositionsCount", variables];
var useInfiniteGetPositionsCountQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetPositionsCount.infinite"] : ["GetPositionsCount.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetPositionsCountDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetPositionsCountQuery.getKey = (variables) => variables === void 0 ? ["GetPositionsCount.infinite"] : ["GetPositionsCount.infinite", variables];
useGetPositionsCountQuery.fetcher = (variables, options) => fetcher(
  GetPositionsCountDocument,
  variables,
  options
);
var GetPositionDocument = `
    query GetPosition($positionId: String!) {
  position(id: $positionId) {
    ...PositionDetails
  }
}
    ${PositionDetailsFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetPositionQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetPosition", variables],
    queryFn: fetcher(
      GetPositionDocument,
      variables
    ),
    ...options
  });
};
useGetPositionQuery.document = GetPositionDocument;
useGetPositionQuery.getKey = (variables) => [
  "GetPosition",
  variables
];
var useInfiniteGetPositionQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetPosition.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetPositionDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetPositionQuery.getKey = (variables) => [
  "GetPosition.infinite",
  variables
];
useGetPositionQuery.fetcher = (variables, options) => fetcher(
  GetPositionDocument,
  variables,
  options
);
var GetPositionsCountByTypeDocument = `
    query GetPositionsCountByType($where: positions_bool_exp) {
  positions_aggregate(where: $where) {
    total: aggregate {
      count
      sum {
        shares
      }
    }
  }
  positions {
    vault {
      term_id
    }
  }
}
    `;
var useGetPositionsCountByTypeQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetPositionsCountByType"] : ["GetPositionsCountByType", variables],
    queryFn: fetcher(GetPositionsCountByTypeDocument, variables),
    ...options
  });
};
useGetPositionsCountByTypeQuery.document = GetPositionsCountByTypeDocument;
useGetPositionsCountByTypeQuery.getKey = (variables) => variables === void 0 ? ["GetPositionsCountByType"] : ["GetPositionsCountByType", variables];
var useInfiniteGetPositionsCountByTypeQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetPositionsCountByType.infinite"] : ["GetPositionsCountByType.infinite", variables],
        queryFn: (metaData) => fetcher(GetPositionsCountByTypeDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetPositionsCountByTypeQuery.getKey = (variables) => variables === void 0 ? ["GetPositionsCountByType.infinite"] : ["GetPositionsCountByType.infinite", variables];
useGetPositionsCountByTypeQuery.fetcher = (variables, options) => fetcher(
  GetPositionsCountByTypeDocument,
  variables,
  options
);
var GetSignalsDocument = `
    query GetSignals($limit: Int, $offset: Int, $orderBy: [signals_order_by!], $addresses: [String!]) {
  total: events_aggregate {
    aggregate {
      count
    }
  }
  signals(limit: $limit, offset: $offset, order_by: $orderBy) {
    id
    block_number
    created_at
    transaction_hash
    atom_id
    triple_id
    deposit_id
    redemption_id
    term {
      atom {
        ...AtomMetadata
        term {
          vaults {
            total_shares
            position_count
            positions(where: {account: {id: {_in: $addresses}}}) {
              account_id
              shares
              account {
                id
                label
                image
              }
            }
          }
        }
      }
      triple {
        term_id
        creator {
          ...AccountMetadata
        }
        subject {
          data
          term_id
          image
          label
          emoji
          type
          ...AtomValue
          creator {
            ...AccountMetadata
          }
        }
        predicate {
          data
          term_id
          image
          label
          emoji
          type
          ...AtomValue
          creator {
            ...AccountMetadata
          }
        }
        object {
          data
          term_id
          image
          label
          emoji
          type
          ...AtomValue
          creator {
            ...AccountMetadata
          }
        }
        term {
          vaults {
            total_shares
            position_count
            positions(where: {account: {id: {_in: $addresses}}}) {
              account_id
              shares
              account {
                id
                label
                image
              }
            }
          }
        }
        counter_term {
          vaults {
            total_shares
            position_count
            positions(where: {account: {id: {_in: $addresses}}}) {
              account_id
              shares
              account {
                id
                label
                image
              }
            }
          }
        }
      }
    }
    deposit {
      sender_id
      sender {
        id
        label
        image
      }
      receiver_id
      receiver {
        id
        label
        image
      }
      shares_for_receiver
      sender_assets_after_total_fees
      vault {
        total_shares
        position_count
        positions(where: {account: {id: {_in: $addresses}}}) {
          account_id
          shares
          account {
            id
            label
            image
          }
        }
      }
    }
    redemption {
      sender_id
      sender {
        id
        label
        image
      }
      receiver_id
      receiver {
        id
        label
        image
      }
      assets_for_receiver
      shares_redeemed_by_sender
    }
  }
}
    ${AtomMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}`;
var useGetSignalsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetSignals"] : ["GetSignals", variables],
    queryFn: fetcher(
      GetSignalsDocument,
      variables
    ),
    ...options
  });
};
useGetSignalsQuery.document = GetSignalsDocument;
useGetSignalsQuery.getKey = (variables) => variables === void 0 ? ["GetSignals"] : ["GetSignals", variables];
var useInfiniteGetSignalsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetSignals.infinite"] : ["GetSignals.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetSignalsDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetSignalsQuery.getKey = (variables) => variables === void 0 ? ["GetSignals.infinite"] : ["GetSignals.infinite", variables];
useGetSignalsQuery.fetcher = (variables, options) => fetcher(
  GetSignalsDocument,
  variables,
  options
);
var GetStatsDocument = `
    query GetStats {
  stats {
    ...StatDetails
  }
}
    ${StatDetailsFragmentDoc}`;
var useGetStatsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetStats"] : ["GetStats", variables],
    queryFn: fetcher(
      GetStatsDocument,
      variables
    ),
    ...options
  });
};
useGetStatsQuery.document = GetStatsDocument;
useGetStatsQuery.getKey = (variables) => variables === void 0 ? ["GetStats"] : ["GetStats", variables];
var useInfiniteGetStatsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetStats.infinite"] : ["GetStats.infinite", variables],
        queryFn: (metaData) => fetcher(GetStatsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetStatsQuery.getKey = (variables) => variables === void 0 ? ["GetStats.infinite"] : ["GetStats.infinite", variables];
useGetStatsQuery.fetcher = (variables, options) => fetcher(
  GetStatsDocument,
  variables,
  options
);
var GetTagsDocument = `
    query GetTags($subjectId: numeric!, $predicateId: numeric!) {
  triples(
    where: {_and: [{subject_id: {_eq: $subjectId}}, {predicate_id: {_eq: $predicateId}}]}
  ) {
    ...TripleMetadata
  }
}
    ${TripleMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}
${PositionAggregateFieldsFragmentDoc}
${PositionFieldsFragmentDoc}`;
var useGetTagsQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetTags", variables],
    queryFn: fetcher(
      GetTagsDocument,
      variables
    ),
    ...options
  });
};
useGetTagsQuery.document = GetTagsDocument;
useGetTagsQuery.getKey = (variables) => [
  "GetTags",
  variables
];
var useInfiniteGetTagsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetTags.infinite", variables],
        queryFn: (metaData) => fetcher(GetTagsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetTagsQuery.getKey = (variables) => [
  "GetTags.infinite",
  variables
];
useGetTagsQuery.fetcher = (variables, options) => fetcher(
  GetTagsDocument,
  variables,
  options
);
var GetTagsCustomDocument = `
    query GetTagsCustom($where: triples_bool_exp) {
  triples(where: $where) {
    ...TripleMetadata
  }
}
    ${TripleMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}
${PositionAggregateFieldsFragmentDoc}
${PositionFieldsFragmentDoc}`;
var useGetTagsCustomQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetTagsCustom"] : ["GetTagsCustom", variables],
    queryFn: fetcher(
      GetTagsCustomDocument,
      variables
    ),
    ...options
  });
};
useGetTagsCustomQuery.document = GetTagsCustomDocument;
useGetTagsCustomQuery.getKey = (variables) => variables === void 0 ? ["GetTagsCustom"] : ["GetTagsCustom", variables];
var useInfiniteGetTagsCustomQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetTagsCustom.infinite"] : ["GetTagsCustom.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetTagsCustomDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetTagsCustomQuery.getKey = (variables) => variables === void 0 ? ["GetTagsCustom.infinite"] : ["GetTagsCustom.infinite", variables];
useGetTagsCustomQuery.fetcher = (variables, options) => fetcher(
  GetTagsCustomDocument,
  variables,
  options
);
var GetTriplesDocument = `
    query GetTriples($limit: Int, $offset: Int, $orderBy: [triples_order_by!], $where: triples_bool_exp) {
  total: triples_aggregate(where: $where) {
    aggregate {
      count
    }
  }
  triples(limit: $limit, offset: $offset, order_by: $orderBy, where: $where) {
    ...TripleMetadata
    ...TripleTxn
    ...TripleVaultDetails
    creator {
      ...AccountMetadata
    }
  }
}
    ${TripleMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}
${PositionAggregateFieldsFragmentDoc}
${PositionFieldsFragmentDoc}
${TripleTxnFragmentDoc}
${TripleVaultDetailsFragmentDoc}
${PositionDetailsFragmentDoc}`;
var useGetTriplesQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetTriples"] : ["GetTriples", variables],
    queryFn: fetcher(
      GetTriplesDocument,
      variables
    ),
    ...options
  });
};
useGetTriplesQuery.document = GetTriplesDocument;
useGetTriplesQuery.getKey = (variables) => variables === void 0 ? ["GetTriples"] : ["GetTriples", variables];
var useInfiniteGetTriplesQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetTriples.infinite"] : ["GetTriples.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetTriplesDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetTriplesQuery.getKey = (variables) => variables === void 0 ? ["GetTriples.infinite"] : ["GetTriples.infinite", variables];
useGetTriplesQuery.fetcher = (variables, options) => fetcher(
  GetTriplesDocument,
  variables,
  options
);
var GetTriplesWithAggregatesDocument = `
    query GetTriplesWithAggregates($limit: Int, $offset: Int, $orderBy: [triples_order_by!], $where: triples_bool_exp) {
  triples_aggregate(
    limit: $limit
    offset: $offset
    order_by: $orderBy
    where: $where
  ) {
    aggregate {
      count
    }
    nodes {
      ...TripleMetadata
      ...TripleTxn
      ...TripleVaultDetails
      creator {
        ...AccountMetadata
      }
    }
  }
}
    ${TripleMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}
${PositionAggregateFieldsFragmentDoc}
${PositionFieldsFragmentDoc}
${TripleTxnFragmentDoc}
${TripleVaultDetailsFragmentDoc}
${PositionDetailsFragmentDoc}`;
var useGetTriplesWithAggregatesQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetTriplesWithAggregates"] : ["GetTriplesWithAggregates", variables],
    queryFn: fetcher(GetTriplesWithAggregatesDocument, variables),
    ...options
  });
};
useGetTriplesWithAggregatesQuery.document = GetTriplesWithAggregatesDocument;
useGetTriplesWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetTriplesWithAggregates"] : ["GetTriplesWithAggregates", variables];
var useInfiniteGetTriplesWithAggregatesQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetTriplesWithAggregates.infinite"] : ["GetTriplesWithAggregates.infinite", variables],
        queryFn: (metaData) => fetcher(GetTriplesWithAggregatesDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetTriplesWithAggregatesQuery.getKey = (variables) => variables === void 0 ? ["GetTriplesWithAggregates.infinite"] : ["GetTriplesWithAggregates.infinite", variables];
useGetTriplesWithAggregatesQuery.fetcher = (variables, options) => fetcher(GetTriplesWithAggregatesDocument, variables, options);
var GetTriplesCountDocument = `
    query GetTriplesCount($where: triples_bool_exp) {
  triples_aggregate(where: $where) {
    total: aggregate {
      count
    }
  }
}
    `;
var useGetTriplesCountQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetTriplesCount"] : ["GetTriplesCount", variables],
    queryFn: fetcher(
      GetTriplesCountDocument,
      variables
    ),
    ...options
  });
};
useGetTriplesCountQuery.document = GetTriplesCountDocument;
useGetTriplesCountQuery.getKey = (variables) => variables === void 0 ? ["GetTriplesCount"] : ["GetTriplesCount", variables];
var useInfiniteGetTriplesCountQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetTriplesCount.infinite"] : ["GetTriplesCount.infinite", variables],
        queryFn: (metaData) => fetcher(
          GetTriplesCountDocument,
          { ...variables, ...metaData.pageParam ?? {} }
        )(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetTriplesCountQuery.getKey = (variables) => variables === void 0 ? ["GetTriplesCount.infinite"] : ["GetTriplesCount.infinite", variables];
useGetTriplesCountQuery.fetcher = (variables, options) => fetcher(
  GetTriplesCountDocument,
  variables,
  options
);
var GetTripleDocument = `
    query GetTriple($tripleId: numeric!) {
  triple(term_id: $tripleId) {
    ...TripleMetadata
    ...TripleTxn
    ...TripleVaultDetails
    creator {
      ...AccountMetadata
    }
  }
}
    ${TripleMetadataFragmentDoc}
${AtomValueFragmentDoc}
${AccountMetadataFragmentDoc}
${PositionAggregateFieldsFragmentDoc}
${PositionFieldsFragmentDoc}
${TripleTxnFragmentDoc}
${TripleVaultDetailsFragmentDoc}
${PositionDetailsFragmentDoc}`;
var useGetTripleQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetTriple", variables],
    queryFn: fetcher(
      GetTripleDocument,
      variables
    ),
    ...options
  });
};
useGetTripleQuery.document = GetTripleDocument;
useGetTripleQuery.getKey = (variables) => [
  "GetTriple",
  variables
];
var useInfiniteGetTripleQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetTriple.infinite", variables],
        queryFn: (metaData) => fetcher(GetTripleDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetTripleQuery.getKey = (variables) => [
  "GetTriple.infinite",
  variables
];
useGetTripleQuery.fetcher = (variables, options) => fetcher(
  GetTripleDocument,
  variables,
  options
);
var GetAtomTriplesWithPositionsDocument = `
    query GetAtomTriplesWithPositions($where: triples_bool_exp) {
  triples_aggregate(where: $where) {
    aggregate {
      count
    }
  }
}
    `;
var useGetAtomTriplesWithPositionsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetAtomTriplesWithPositions"] : ["GetAtomTriplesWithPositions", variables],
    queryFn: fetcher(GetAtomTriplesWithPositionsDocument, variables),
    ...options
  });
};
useGetAtomTriplesWithPositionsQuery.document = GetAtomTriplesWithPositionsDocument;
useGetAtomTriplesWithPositionsQuery.getKey = (variables) => variables === void 0 ? ["GetAtomTriplesWithPositions"] : ["GetAtomTriplesWithPositions", variables];
var useInfiniteGetAtomTriplesWithPositionsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetAtomTriplesWithPositions.infinite"] : ["GetAtomTriplesWithPositions.infinite", variables],
        queryFn: (metaData) => fetcher(GetAtomTriplesWithPositionsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetAtomTriplesWithPositionsQuery.getKey = (variables) => variables === void 0 ? ["GetAtomTriplesWithPositions.infinite"] : ["GetAtomTriplesWithPositions.infinite", variables];
useGetAtomTriplesWithPositionsQuery.fetcher = (variables, options) => fetcher(GetAtomTriplesWithPositionsDocument, variables, options);
var GetTriplesWithPositionsDocument = `
    query GetTriplesWithPositions($limit: Int, $offset: Int, $orderBy: [triples_order_by!], $where: triples_bool_exp, $address: String) {
  total: triples_aggregate(where: $where) {
    aggregate {
      count
    }
  }
  triples(limit: $limit, offset: $offset, order_by: $orderBy, where: $where) {
    term_id
    counter_term_id
    subject {
      term_id
      label
      image
    }
    predicate {
      term_id
      label
      image
    }
    object {
      term_id
      label
      image
    }
    term {
      vaults {
        total_shares
        position_count
        positions(where: {account_id: {_ilike: $address}}) {
          account {
            id
            label
            image
          }
          shares
        }
      }
    }
    counter_term {
      vaults {
        total_shares
        position_count
        positions(where: {account_id: {_ilike: $address}}) {
          account {
            id
            label
            image
          }
          shares
        }
      }
    }
  }
}
    `;
var useGetTriplesWithPositionsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetTriplesWithPositions"] : ["GetTriplesWithPositions", variables],
    queryFn: fetcher(GetTriplesWithPositionsDocument, variables),
    ...options
  });
};
useGetTriplesWithPositionsQuery.document = GetTriplesWithPositionsDocument;
useGetTriplesWithPositionsQuery.getKey = (variables) => variables === void 0 ? ["GetTriplesWithPositions"] : ["GetTriplesWithPositions", variables];
var useInfiniteGetTriplesWithPositionsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetTriplesWithPositions.infinite"] : ["GetTriplesWithPositions.infinite", variables],
        queryFn: (metaData) => fetcher(GetTriplesWithPositionsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetTriplesWithPositionsQuery.getKey = (variables) => variables === void 0 ? ["GetTriplesWithPositions.infinite"] : ["GetTriplesWithPositions.infinite", variables];
useGetTriplesWithPositionsQuery.fetcher = (variables, options) => fetcher(
  GetTriplesWithPositionsDocument,
  variables,
  options
);
var GetVaultsDocument = `
    query GetVaults($limit: Int, $offset: Int, $orderBy: [vaults_order_by!], $where: vaults_bool_exp) {
  vaults_aggregate(
    limit: $limit
    offset: $offset
    order_by: $orderBy
    where: $where
  ) {
    aggregate {
      count
    }
    nodes {
      term_id
      term {
        atom {
          term_id
          label
        }
        triple {
          term_id
          subject {
            term_id
            label
          }
          predicate {
            term_id
            label
          }
          object {
            term_id
            label
          }
        }
      }
      positions_aggregate {
        nodes {
          account {
            atom_id
            label
          }
          shares
        }
      }
      current_share_price
      total_shares
    }
  }
}
    `;
var useGetVaultsQuery = (variables, options) => {
  return useQuery({
    queryKey: variables === void 0 ? ["GetVaults"] : ["GetVaults", variables],
    queryFn: fetcher(
      GetVaultsDocument,
      variables
    ),
    ...options
  });
};
useGetVaultsQuery.document = GetVaultsDocument;
useGetVaultsQuery.getKey = (variables) => variables === void 0 ? ["GetVaults"] : ["GetVaults", variables];
var useInfiniteGetVaultsQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? variables === void 0 ? ["GetVaults.infinite"] : ["GetVaults.infinite", variables],
        queryFn: (metaData) => fetcher(GetVaultsDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetVaultsQuery.getKey = (variables) => variables === void 0 ? ["GetVaults.infinite"] : ["GetVaults.infinite", variables];
useGetVaultsQuery.fetcher = (variables, options) => fetcher(
  GetVaultsDocument,
  variables,
  options
);
var GetVaultDocument = `
    query GetVault($termId: numeric!, $curveId: numeric!) {
  vault(term_id: $termId, curve_id: $curveId) {
    ...VaultDetails
  }
}
    ${VaultDetailsFragmentDoc}
${VaultBasicDetailsFragmentDoc}`;
var useGetVaultQuery = (variables, options) => {
  return useQuery({
    queryKey: ["GetVault", variables],
    queryFn: fetcher(
      GetVaultDocument,
      variables
    ),
    ...options
  });
};
useGetVaultQuery.document = GetVaultDocument;
useGetVaultQuery.getKey = (variables) => [
  "GetVault",
  variables
];
var useInfiniteGetVaultQuery = (variables, options) => {
  return useInfiniteQuery(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options;
      return {
        queryKey: optionsQueryKey ?? ["GetVault.infinite", variables],
        queryFn: (metaData) => fetcher(GetVaultDocument, {
          ...variables,
          ...metaData.pageParam ?? {}
        })(),
        ...restOptions
      };
    })()
  );
};
useInfiniteGetVaultQuery.getKey = (variables) => [
  "GetVault.infinite",
  variables
];
useGetVaultQuery.fetcher = (variables, options) => fetcher(
  GetVaultDocument,
  variables,
  options
);
var AccountPositionsAggregate = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountPositionsAggregate" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "shares" },
                      value: { kind: "EnumValue", value: "desc" }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vault" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "atom" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "triple" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AccountPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "shares" },
                      value: { kind: "EnumValue", value: "desc" }
                    }
                  ]
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsOffset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "shares" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vault" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "atom" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "triple" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AccountAtoms = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountAtoms" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "data" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "positions_aggregate"
                              },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "address"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "nodes" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "id"
                                                }
                                              }
                                            ]
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AccountAtomsAggregate = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountAtomsAggregate" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sum" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "vaults" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "curve_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "StringValue",
                                                value: "1",
                                                block: false
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "total_shares"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "positions_aggregate"
                                    },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "account_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "Variable",
                                                      name: {
                                                        kind: "Name",
                                                        value: "address"
                                                      }
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "nodes"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "id"
                                                      }
                                                    }
                                                  ]
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AccountTriples = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountTriples" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AccountTriplesAggregate = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountTriplesAggregate" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AtomTxn = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    }
  ]
};
var AtomVaultDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "position_count" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "sum" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AccountMetadata = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    }
  ]
};
var AtomTriple = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTriple" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "as_subject_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "as_predicate_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "as_object_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    }
  ]
};
var AtomVaultDetailsWithPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomVaultDetailsWithPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "account_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_in" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "addresses"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "sum" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "nodes" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var DepositEventFragment = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DepositEventFragment" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "events" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "deposit" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "curve_id" } },
                {
                  kind: "Field",
                  name: {
                    kind: "Name",
                    value: "sender_assets_after_total_fees"
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "shares_for_receiver" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "receiver" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sender" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var RedemptionEventFragment = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RedemptionEventFragment" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "events" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "redemption" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "curve_id" } },
                { kind: "Field", name: { kind: "Name", value: "receiver_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "shares_redeemed_by_sender" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "assets_for_receiver" }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AtomValue = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AtomMetadata = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var PositionAggregateFields = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var PositionFields = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var TripleMetadata = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "subject_id" } },
          { kind: "Field", name: { kind: "Name", value: "predicate_id" } },
          { kind: "Field", name: { kind: "Name", value: "object_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var EventDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "EventDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "events" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "triple_id" } },
          { kind: "Field", name: { kind: "Name", value: "deposit_id" } },
          { kind: "Field", name: { kind: "Name", value: "redemption_id" } },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "DepositEventFragment" }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "RedemptionEventFragment" }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "atom" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomMetadata" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "triple" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleMetadata" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DepositEventFragment" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "events" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "deposit" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "curve_id" } },
                {
                  kind: "Field",
                  name: {
                    kind: "Name",
                    value: "sender_assets_after_total_fees"
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "shares_for_receiver" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "receiver" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sender" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RedemptionEventFragment" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "events" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "redemption" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "curve_id" } },
                { kind: "Field", name: { kind: "Name", value: "receiver_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "shares_redeemed_by_sender" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "assets_for_receiver" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "subject_id" } },
          { kind: "Field", name: { kind: "Name", value: "predicate_id" } },
          { kind: "Field", name: { kind: "Name", value: "object_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var FollowMetadata = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "FollowMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsWhere" }
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "sum" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "limit" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsLimit" }
                            }
                          },
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "offset" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsOffset" }
                            }
                          },
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "order_by" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsOrderBy" }
                            }
                          },
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsWhere" }
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var FollowAggregate = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "FollowAggregate" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } }
              ]
            }
          }
        ]
      }
    }
  ]
};
var StatDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "StatDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "stats" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "contract_balance" } },
          { kind: "Field", name: { kind: "Name", value: "total_accounts" } },
          { kind: "Field", name: { kind: "Name", value: "total_fees" } },
          { kind: "Field", name: { kind: "Name", value: "total_atoms" } },
          { kind: "Field", name: { kind: "Name", value: "total_triples" } },
          { kind: "Field", name: { kind: "Name", value: "total_positions" } },
          { kind: "Field", name: { kind: "Name", value: "total_signals" } }
        ]
      }
    }
  ]
};
var TripleTxn = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    }
  ]
};
var PositionDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var TripleVaultDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "counter_term_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    }
  ]
};
var VaultBasicDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultBasicDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "triple" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "current_share_price" }
          },
          { kind: "Field", name: { kind: "Name", value: "total_shares" } }
        ]
      }
    }
  ]
};
var VaultFilteredPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultFilteredPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "account_id" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_in" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "addresses" }
                            }
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionFields" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var VaultDetailsWithFilteredPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultDetailsWithFilteredPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "VaultBasicDetails" }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "VaultFilteredPositions" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultBasicDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "triple" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "current_share_price" }
          },
          { kind: "Field", name: { kind: "Name", value: "total_shares" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultFilteredPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "account_id" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_in" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "addresses" }
                            }
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionFields" }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var TripleVaultCouterVaultDetailsWithPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: {
        kind: "Name",
        value: "TripleVaultCouterVaultDetailsWithPositions"
      },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "counter_term_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: {
                          kind: "Name",
                          value: "VaultDetailsWithFilteredPositions"
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: {
                          kind: "Name",
                          value: "VaultDetailsWithFilteredPositions"
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultBasicDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "triple" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "current_share_price" }
          },
          { kind: "Field", name: { kind: "Name", value: "total_shares" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultFilteredPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "account_id" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_in" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "addresses" }
                            }
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionFields" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultDetailsWithFilteredPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "VaultBasicDetails" }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "VaultFilteredPositions" }
          }
        ]
      }
    }
  ]
};
var VaultUnfilteredPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultUnfilteredPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionFields" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var VaultDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "VaultBasicDetails" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultBasicDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "triple" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "current_share_price" }
          },
          { kind: "Field", name: { kind: "Name", value: "total_shares" } }
        ]
      }
    }
  ]
};
var VaultPositionsAggregate = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultPositionsAggregate" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions_aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionAggregateFields" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var VaultFieldsForTriple = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultFieldsForTriple" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "total_shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "current_share_price" }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "VaultPositionsAggregate" }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "VaultFilteredPositions" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultPositionsAggregate" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions_aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionAggregateFields" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultFilteredPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "account_id" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_in" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "addresses" }
                            }
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionFields" }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var AtomMetadataMaybedeletethis = {
  kind: "Document",
  definitions: [
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadataMAYBEDELETETHIS" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var PinThing = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "mutation",
      name: { kind: "Name", value: "pinThing" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "name" } },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "description" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "image" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } }
        },
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "url" } },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "pinThing" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "thing" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "description" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "description" }
                      }
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "image" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "image" }
                      }
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "name" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "name" }
                      }
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "url" },
                      value: {
                        kind: "Variable",
                        name: { kind: "Name", value: "url" }
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "uri" } }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAccounts = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAccounts" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "accounts_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "accounts_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "accounts" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountMetadata" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountPositions" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "vaults" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "curve_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "StringValue",
                                                value: "1",
                                                block: false
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "position_count"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "total_shares"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "positions_aggregate"
                                    },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "count"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "sum"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "shares"
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "positions" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "label"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "id"
                                                }
                                              }
                                            ]
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "shares" },
                      value: { kind: "EnumValue", value: "desc" }
                    }
                  ]
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsOffset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "shares" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vault" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "atom" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "triple" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAccountsWithAggregates = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAccountsWithAggregates" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "accounts_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "accounts_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "atoms_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "atoms_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "accounts_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      },
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountPositions" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "shares" },
                      value: { kind: "EnumValue", value: "desc" }
                    }
                  ]
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsOffset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "shares" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vault" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "atom" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "triple" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAccountsCount = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAccountsCount" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "accounts_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "accounts_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAccount = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAccount" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "atoms_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "atoms_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "address" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountMetadata" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      },
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomVaultDetails" }
                      }
                    ]
                  }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountPositions" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountAtoms" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountTriples" }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "chainlink_prices" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: { kind: "IntValue", value: "1" }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "id" },
                      value: { kind: "EnumValue", value: "desc" }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "usd" } }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "shares" },
                      value: { kind: "EnumValue", value: "desc" }
                    }
                  ]
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsOffset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "shares" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vault" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "atom" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "triple" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountAtoms" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "data" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "positions_aggregate"
                              },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "address"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "nodes" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "id"
                                                }
                                              }
                                            ]
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountTriples" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "position_count" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "sum" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAccountWithPaginatedRelations = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAccountWithPaginatedRelations" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "atoms_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "atoms_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "address" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountMetadata" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountPositions" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountAtoms" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountTriples" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountPositions" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "shares" },
                      value: { kind: "EnumValue", value: "desc" }
                    }
                  ]
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsOffset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionsWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "shares" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vault" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "atom" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "triple" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountAtoms" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "data" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "positions_aggregate"
                              },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "address"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "nodes" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "id"
                                                }
                                              }
                                            ]
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountTriples" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAccountWithAggregates = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAccountWithAggregates" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "atoms_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "atoms_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "atomsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "triplesOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "address" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountMetadata" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountPositionsAggregate" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountAtomsAggregate" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountTriplesAggregate" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountPositionsAggregate" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "shares" },
                      value: { kind: "EnumValue", value: "desc" }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vault" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "atom" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "triple" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountAtomsAggregate" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "atomsOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sum" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "vaults" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "curve_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "StringValue",
                                                value: "1",
                                                block: false
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "total_shares"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "positions_aggregate"
                                    },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "account_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "Variable",
                                                      name: {
                                                        kind: "Name",
                                                        value: "address"
                                                      }
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "nodes"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "id"
                                                      }
                                                    }
                                                  ]
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountTriplesAggregate" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOrderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesLimit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "triplesOffset" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAccountMetadata = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAccountMetadata" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "address" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AccountMetadata" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    }
  ]
};
var GetAtoms = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAtoms" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "atoms_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "atoms_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "total" },
            name: { kind: "Name", value: "atoms_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomMetadata" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomTxn" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomVaultDetails" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomTriple" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "position_count" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "sum" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTriple" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "as_subject_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "as_predicate_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "as_object_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAtomsWithPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAtomsWithPositions" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "atoms_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "atoms_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "total" },
            name: { kind: "Name", value: "atoms_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomMetadata" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomTxn" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              alias: { kind: "Name", value: "total" },
                              name: {
                                kind: "Name",
                                value: "positions_aggregate"
                              },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "aggregate" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "sum" },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_ilike"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "address"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    }
  ]
};
var GetAtomsWithAggregates = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAtomsWithAggregates" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "atoms_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "atoms_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      },
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomTxn" }
                      },
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomVaultDetails" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "position_count" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "sum" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAtomsCount = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAtomsCount" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "atoms_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAtom = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAtom" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atom" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "term_id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomMetadata" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomTxn" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomVaultDetails" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomTriple" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "position_count" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "sum" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTriple" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "as_subject_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "as_predicate_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "as_object_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAtomByData = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAtomByData" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "data" } },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atoms" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "data" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_eq" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "data" }
                            }
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomMetadata" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomTxn" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomVaultDetails" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomTriple" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "position_count" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "sum" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomTriple" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "as_subject_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "as_predicate_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "as_object_triples" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "data" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "emoji" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetVerifiedAtomDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetVerifiedAtomDetails" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "userPositionAddress" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atom" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "term_id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                { kind: "Field", name: { kind: "Name", value: "created_at" } },
                { kind: "Field", name: { kind: "Name", value: "data" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "value" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "thing" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "name" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "description" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "url" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                },
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "term_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "Variable",
                                          name: { kind: "Name", value: "id" }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              alias: { kind: "Name", value: "userPosition" },
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "limit" },
                                  value: { kind: "IntValue", value: "1" }
                                },
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_ilike"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "userPositionAddress"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  alias: { kind: "Name", value: "tags" },
                  name: { kind: "Name", value: "as_subject_triples_aggregate" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "predicate_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_in" },
                                  value: {
                                    kind: "ListValue",
                                    values: [{ kind: "IntValue", value: "3" }]
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "nodes" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "vaults"
                                          },
                                          arguments: [
                                            {
                                              kind: "Argument",
                                              name: {
                                                kind: "Name",
                                                value: "where"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "curve_id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_eq"
                                                          },
                                                          value: {
                                                            kind: "StringValue",
                                                            value: "1",
                                                            block: false
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  },
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "term_id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_eq"
                                                          },
                                                          value: {
                                                            kind: "Variable",
                                                            name: {
                                                              kind: "Name",
                                                              value: "id"
                                                            }
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ],
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "term_id"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate_id" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "count" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  alias: { kind: "Name", value: "verificationTriple" },
                  name: { kind: "Name", value: "as_subject_triples_aggregate" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "predicate_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "4",
                                    block: false
                                  }
                                }
                              ]
                            }
                          },
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "object_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "126451",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "nodes" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions"
                                          },
                                          arguments: [
                                            {
                                              kind: "Argument",
                                              name: {
                                                kind: "Name",
                                                value: "where"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "account_id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_in"
                                                          },
                                                          value: {
                                                            kind: "ListValue",
                                                            values: [
                                                              {
                                                                kind: "StringValue",
                                                                value: "0xD99811847E634d33f0DAcE483c52949bEc76300F",
                                                                block: false
                                                              },
                                                              {
                                                                kind: "StringValue",
                                                                value: "0xBb285b543C96C927FC320Fb28524899C2C90806C",
                                                                block: false
                                                              },
                                                              {
                                                                kind: "StringValue",
                                                                value: "0x0b162525C5dc8c18F771E60fD296913030Bfe42c",
                                                                block: false
                                                              },
                                                              {
                                                                kind: "StringValue",
                                                                value: "0xbd2DE08aF9470c87C4475117Fb912B8f1d588D9c",
                                                                block: false
                                                              },
                                                              {
                                                                kind: "StringValue",
                                                                value: "0xB95ca3D3144e9d1DAFF0EE3d35a4488A4A5C9Fc5",
                                                                block: false
                                                              }
                                                            ]
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ],
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "id"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account_id"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "id"
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAtomDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAtomDetails" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: { kind: "Variable", name: { kind: "Name", value: "id" } },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "userPositionAddress" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "atom" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "term_id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "id" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                { kind: "Field", name: { kind: "Name", value: "created_at" } },
                { kind: "Field", name: { kind: "Name", value: "data" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "value" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "thing" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "name" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "description" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "url" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                },
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "term_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "Variable",
                                          name: { kind: "Name", value: "id" }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              alias: { kind: "Name", value: "userPosition" },
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "limit" },
                                  value: { kind: "IntValue", value: "1" }
                                },
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "userPositionAddress"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  alias: { kind: "Name", value: "tags" },
                  name: { kind: "Name", value: "as_subject_triples_aggregate" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "predicate_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_in" },
                                  value: {
                                    kind: "ListValue",
                                    values: [{ kind: "IntValue", value: "3" }]
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "nodes" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "vaults"
                                          },
                                          arguments: [
                                            {
                                              kind: "Argument",
                                              name: {
                                                kind: "Name",
                                                value: "where"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "curve_id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_eq"
                                                          },
                                                          value: {
                                                            kind: "StringValue",
                                                            value: "1",
                                                            block: false
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  },
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "term_id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_eq"
                                                          },
                                                          value: {
                                                            kind: "Variable",
                                                            name: {
                                                              kind: "Name",
                                                              value: "id"
                                                            }
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ],
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "term_id"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate_id" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "count" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetEvents = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetEvents" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "events_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "events_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "addresses" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "String" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "total" },
            name: { kind: "Name", value: "events_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "events" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "block_number" }
                },
                { kind: "Field", name: { kind: "Name", value: "created_at" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "transaction_hash" }
                },
                { kind: "Field", name: { kind: "Name", value: "atom_id" } },
                { kind: "Field", name: { kind: "Name", value: "triple_id" } },
                { kind: "Field", name: { kind: "Name", value: "deposit_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "redemption_id" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "vaults" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "curve_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "StringValue",
                                                value: "1",
                                                block: false
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "total_shares"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "position_count"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "positions" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "account"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_in"
                                                          },
                                                          value: {
                                                            kind: "Variable",
                                                            name: {
                                                              kind: "Name",
                                                              value: "addresses"
                                                            }
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "id"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "label"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "image"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "triple" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "data" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "emoji" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "type" }
                            },
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AtomValue" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "creator" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "FragmentSpread",
                                    name: {
                                      kind: "Name",
                                      value: "AccountMetadata"
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "data" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "emoji" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "type" }
                            },
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AtomValue" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "creator" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "FragmentSpread",
                                    name: {
                                      kind: "Name",
                                      value: "AccountMetadata"
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "data" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "emoji" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "type" }
                            },
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AtomValue" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "creator" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "FragmentSpread",
                                    name: {
                                      kind: "Name",
                                      value: "AccountMetadata"
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "vaults" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "curve_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "StringValue",
                                                value: "1",
                                                block: false
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "total_shares"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "position_count"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "positions" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "account"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_in"
                                                          },
                                                          value: {
                                                            kind: "Variable",
                                                            name: {
                                                              kind: "Name",
                                                              value: "addresses"
                                                            }
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "id"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "label"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "image"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "counter_term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "vaults" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "curve_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "StringValue",
                                                value: "1",
                                                block: false
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "total_shares"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "position_count"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "positions" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "account"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_in"
                                                          },
                                                          value: {
                                                            kind: "Variable",
                                                            name: {
                                                              kind: "Name",
                                                              value: "addresses"
                                                            }
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "account"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "id"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "label"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "image"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "deposit" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sender_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sender" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares_for_receiver" }
                      },
                      {
                        kind: "Field",
                        name: {
                          kind: "Name",
                          value: "sender_assets_after_total_fees"
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vault" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_in"
                                                    },
                                                    value: {
                                                      kind: "Variable",
                                                      name: {
                                                        kind: "Name",
                                                        value: "addresses"
                                                      }
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "redemption" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sender_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sender" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    }
  ]
};
var GetEventsWithAggregates = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetEventsWithAggregates" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "events_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "events_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "addresses" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "String" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "events_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "max" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "created_at" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "block_number" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "min" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "created_at" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "block_number" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "EventDetails" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "DepositEventFragment" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "events" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "deposit" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "curve_id" } },
                {
                  kind: "Field",
                  name: {
                    kind: "Name",
                    value: "sender_assets_after_total_fees"
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "shares_for_receiver" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "receiver" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sender" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "EventDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "events" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "triple_id" } },
          { kind: "Field", name: { kind: "Name", value: "deposit_id" } },
          { kind: "Field", name: { kind: "Name", value: "redemption_id" } },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "DepositEventFragment" }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "RedemptionEventFragment" }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "atom" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomMetadata" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "triple" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleMetadata" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "RedemptionEventFragment" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "events" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "redemption" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "curve_id" } },
                { kind: "Field", name: { kind: "Name", value: "receiver_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "shares_redeemed_by_sender" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "assets_for_receiver" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "subject_id" } },
          { kind: "Field", name: { kind: "Name", value: "predicate_id" } },
          { kind: "Field", name: { kind: "Name", value: "object_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetEventsCount = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetEventsCount" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "events_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "events_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetEventsData = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetEventsData" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "events_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "events_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "max" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "created_at" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "block_number" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "min" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "created_at" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "block_number" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "avg" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "block_number" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetDebugEvents = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetDebugEvents" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "addresses" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "String" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "debug_events" },
            name: { kind: "Name", value: "events" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_in"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "addresses"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetFollowingPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetFollowingPositions" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "subjectId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "predicateId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "positions_order_by" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "positions" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: {
                                        kind: "Name",
                                        value: "account_id"
                                      },
                                      value: {
                                        kind: "ObjectValue",
                                        fields: [
                                          {
                                            kind: "ObjectField",
                                            name: {
                                              kind: "Name",
                                              value: "_ilike"
                                            },
                                            value: {
                                              kind: "Variable",
                                              name: {
                                                kind: "Name",
                                                value: "address"
                                              }
                                            }
                                          }
                                        ]
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "positions" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: {
                                        kind: "Name",
                                        value: "account_id"
                                      },
                                      value: {
                                        kind: "ObjectValue",
                                        fields: [
                                          {
                                            kind: "ObjectField",
                                            name: {
                                              kind: "Name",
                                              value: "_ilike"
                                            },
                                            value: {
                                              kind: "Variable",
                                              name: {
                                                kind: "Name",
                                                value: "address"
                                              }
                                            }
                                          }
                                        ]
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "positions_aggregate"
                              },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "aggregate" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "sum" },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_ilike"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "address"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                },
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "order_by" },
                                  value: {
                                    kind: "Variable",
                                    name: {
                                      kind: "Name",
                                      value: "positionsOrderBy"
                                    }
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    }
  ]
};
var GetFollowerPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetFollowerPositions" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "subjectId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "predicateId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "objectId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "positions_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "object_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "objectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AtomMetadata" }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "positions_aggregate"
                              },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "aggregate" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "sum" },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "limit" },
                                  value: {
                                    kind: "Variable",
                                    name: {
                                      kind: "Name",
                                      value: "positionsLimit"
                                    }
                                  }
                                },
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "offset" },
                                  value: {
                                    kind: "Variable",
                                    name: {
                                      kind: "Name",
                                      value: "positionsOffset"
                                    }
                                  }
                                },
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "order_by" },
                                  value: {
                                    kind: "Variable",
                                    name: {
                                      kind: "Name",
                                      value: "positionsOrderBy"
                                    }
                                  }
                                },
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "Variable",
                                    name: {
                                      kind: "Name",
                                      value: "positionsWhere"
                                    }
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    }
  ]
};
var GetConnections = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetConnections" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "subjectId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "predicateId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "objectId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "addresses" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "String" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsLimit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOffset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsOrderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "positions_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionsWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "following_count" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "object_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "objectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "following" },
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "object_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "objectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "FollowMetadata" }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "followers_count" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "positions" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: {
                                        kind: "Name",
                                        value: "account_id"
                                      },
                                      value: {
                                        kind: "ObjectValue",
                                        fields: [
                                          {
                                            kind: "ObjectField",
                                            name: {
                                              kind: "Name",
                                              value: "_in"
                                            },
                                            value: {
                                              kind: "Variable",
                                              name: {
                                                kind: "Name",
                                                value: "addresses"
                                              }
                                            }
                                          }
                                        ]
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "followers" },
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "positions" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: {
                                        kind: "Name",
                                        value: "account_id"
                                      },
                                      value: {
                                        kind: "ObjectValue",
                                        fields: [
                                          {
                                            kind: "ObjectField",
                                            name: {
                                              kind: "Name",
                                              value: "_in"
                                            },
                                            value: {
                                              kind: "Variable",
                                              name: {
                                                kind: "Name",
                                                value: "addresses"
                                              }
                                            }
                                          }
                                        ]
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "FollowMetadata" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "FollowMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsWhere" }
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "sum" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "shares"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "limit" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsLimit" }
                            }
                          },
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "offset" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsOffset" }
                            }
                          },
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "order_by" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsOrderBy" }
                            }
                          },
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "positionsWhere" }
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetConnectionsCount = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetConnectionsCount" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "subjectId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "predicateId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "objectId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "following_count" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "positions" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: {
                                        kind: "Name",
                                        value: "account_id"
                                      },
                                      value: {
                                        kind: "ObjectValue",
                                        fields: [
                                          {
                                            kind: "ObjectField",
                                            name: {
                                              kind: "Name",
                                              value: "_ilike"
                                            },
                                            value: {
                                              kind: "Variable",
                                              name: {
                                                kind: "Name",
                                                value: "address"
                                              }
                                            }
                                          }
                                        ]
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "followers_count" },
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "object_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "objectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "positions_aggregate"
                              },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "aggregate" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "sum" },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetLists = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetLists" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "predicate_objects_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate_objects_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate_objects" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ListValue",
                  values: [
                    {
                      kind: "ObjectValue",
                      fields: [
                        {
                          kind: "ObjectField",
                          name: { kind: "Name", value: "triple_count" },
                          value: { kind: "EnumValue", value: "desc" }
                        }
                      ]
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "triple_count" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetListItems = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetListItems" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "predicateId" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "numeric" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "objectId" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "numeric" } }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "predicate_id" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_eq" },
                            value: { kind: "EnumValue", value: "predicateId" }
                          }
                        ]
                      }
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "object_id" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_eq" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "objectId" }
                            }
                          }
                        ]
                      }
                    }
                  ]
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "ListValue",
                  values: [
                    {
                      kind: "ObjectValue",
                      fields: [
                        {
                          kind: "ObjectField",
                          name: { kind: "Name", value: "term" },
                          value: {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: {
                                  kind: "Name",
                                  value: "positions_aggregate"
                                },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "count" },
                                      value: {
                                        kind: "EnumValue",
                                        value: "desc"
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        },
                        {
                          kind: "ObjectField",
                          name: { kind: "Name", value: "counter_term" },
                          value: {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: {
                                  kind: "Name",
                                  value: "positions_aggregate"
                                },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "count" },
                                      value: {
                                        kind: "EnumValue",
                                        value: "desc"
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        }
                      ]
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "TripleVaultDetails" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "counter_term_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetListDetails = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetListDetails" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "globalWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "tagPredicateId" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "numeric" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "globalTriplesAggregate" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "globalWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "globalTriples" },
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "globalWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term_id" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "vaults" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "position_count"
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "tags" },
                        name: {
                          kind: "Name",
                          value: "as_subject_triples_aggregate"
                        },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "predicate_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "tagPredicateId"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "nodes" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "object" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          alias: {
                                            kind: "Name",
                                            value: "taggedIdentities"
                                          },
                                          name: {
                                            kind: "Name",
                                            value: "as_object_triples_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "nodes"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "subject"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "label"
                                                            }
                                                          },
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "term_id"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "term_id"
                                                      }
                                                    }
                                                  ]
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "count"
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetListDetailsWithPosition = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetListDetailsWithPosition" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "globalWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "tagPredicateId" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "numeric" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "globalTriplesAggregate" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "globalWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "globalTriples" },
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "globalWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term_id" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "tags" },
                        name: {
                          kind: "Name",
                          value: "as_subject_triples_aggregate"
                        },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "predicate_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "tagPredicateId"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "nodes" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "object" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          alias: {
                                            kind: "Name",
                                            value: "taggedIdentities"
                                          },
                                          name: {
                                            kind: "Name",
                                            value: "as_object_triples_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "nodes"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "subject"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "label"
                                                            }
                                                          },
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "term_id"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "term_id"
                                                      }
                                                    }
                                                  ]
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "count"
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "account_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_ilike" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "address"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "account_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_ilike" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "address"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetListDetailsWithUser = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetListDetailsWithUser" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "globalWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "userWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "tagPredicateId" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "numeric" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "globalTriplesAggregate" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "globalWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "globalTriples" },
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "globalWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term_id" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "tags" },
                        name: {
                          kind: "Name",
                          value: "as_subject_triples_aggregate"
                        },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "predicate_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "tagPredicateId"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "nodes" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "object" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          alias: {
                                            kind: "Name",
                                            value: "taggedIdentities"
                                          },
                                          name: {
                                            kind: "Name",
                                            value: "as_object_triples_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "nodes"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "subject"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "label"
                                                            }
                                                          },
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "term_id"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "term_id"
                                                      }
                                                    }
                                                  ]
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "count"
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "account_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_ilike" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "address"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "account_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_ilike" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "address"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "userTriplesAggregate" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "userWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "userTriples" },
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "userWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term_id" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "tags" },
                        name: {
                          kind: "Name",
                          value: "as_subject_triples_aggregate"
                        },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "predicate_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "tagPredicateId"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "nodes" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "object" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          alias: {
                                            kind: "Name",
                                            value: "taggedIdentities"
                                          },
                                          name: {
                                            kind: "Name",
                                            value: "as_object_triples_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "nodes"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "subject"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "label"
                                                            }
                                                          },
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "term_id"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "term_id"
                                                      }
                                                    }
                                                  ]
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "count"
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "aggregate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "count" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "account_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_ilike" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "address"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "account_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_ilike" },
                                        value: {
                                          kind: "Variable",
                                          name: {
                                            kind: "Name",
                                            value: "address"
                                          }
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "account" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetListDetailsSimplified = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetListDetailsSimplified" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "globalWhere" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "globalTriplesAggregate" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "globalWhere" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "globalTriples" },
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "globalWhere" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term_id" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "vaults" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "curve_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_eq"
                                              },
                                              value: {
                                                kind: "StringValue",
                                                value: "1",
                                                block: false
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "current_share_price"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "position_count"
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "total_shares"
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "wallet_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      { kind: "Field", name: { kind: "Name", value: "type" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_ilike"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "address"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        arguments: [
                          {
                            kind: "Argument",
                            name: { kind: "Name", value: "where" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "curve_id" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: { kind: "Name", value: "_eq" },
                                        value: {
                                          kind: "StringValue",
                                          value: "1",
                                          block: false
                                        }
                                      }
                                    ]
                                  }
                                }
                              ]
                            }
                          }
                        ],
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: {
                                kind: "Name",
                                value: "current_share_price"
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_ilike"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "address"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetFeeTransfers = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetFeeTransfers" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "cutoff_timestamp" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "timestamptz" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "before_cutoff" },
            name: { kind: "Name", value: "fee_transfers_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "created_at" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_lte" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "cutoff_timestamp" }
                            }
                          }
                        ]
                      }
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "sender_id" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_ilike" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "address" }
                            }
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sum" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "amount" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            alias: { kind: "Name", value: "after_cutoff" },
            name: { kind: "Name", value: "fee_transfers_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "created_at" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_gt" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "cutoff_timestamp" }
                            }
                          }
                        ]
                      }
                    },
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "sender_id" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "_ilike" },
                            value: {
                              kind: "Variable",
                              name: { kind: "Name", value: "address" }
                            }
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sum" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "amount" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetPositions" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "positions_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "total" },
            name: { kind: "Name", value: "positions_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sum" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionDetails" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    }
  ]
};
var GetTriplePositionsByAddress = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetTriplePositionsByAddress" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "positions_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "total" },
            name: { kind: "Name", value: "positions_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sum" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionDetails" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vault" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "triple" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions"
                                          },
                                          arguments: [
                                            {
                                              kind: "Argument",
                                              name: {
                                                kind: "Name",
                                                value: "where"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "account_id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_ilike"
                                                          },
                                                          value: {
                                                            kind: "Variable",
                                                            name: {
                                                              kind: "Name",
                                                              value: "address"
                                                            }
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ],
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "id"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "label"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "image"
                                                      }
                                                    }
                                                  ]
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: {
                                      kind: "Name",
                                      value: "counter_term"
                                    },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions"
                                          },
                                          arguments: [
                                            {
                                              kind: "Argument",
                                              name: {
                                                kind: "Name",
                                                value: "where"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "account_id"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "_ilike"
                                                          },
                                                          value: {
                                                            kind: "Variable",
                                                            name: {
                                                              kind: "Name",
                                                              value: "address"
                                                            }
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ],
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "id"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "label"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "image"
                                                      }
                                                    }
                                                  ]
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    }
  ]
};
var GetPositionsWithAggregates = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetPositionsWithAggregates" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "positions_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "PositionDetails" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    }
  ]
};
var GetPositionsCount = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetPositionsCount" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  alias: { kind: "Name", value: "total" },
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sum" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetPosition = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetPosition" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "positionId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "String" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "position" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "positionId" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "PositionDetails" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    }
  ]
};
var GetPositionsCountByType = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetPositionsCountByType" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "positions_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "positions_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  alias: { kind: "Name", value: "total" },
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sum" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "shares" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "positions" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vault" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetSignals = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetSignals" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "signals_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "addresses" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "String" }
              }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "total" },
            name: { kind: "Name", value: "events_aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "signals" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "block_number" }
                },
                { kind: "Field", name: { kind: "Name", value: "created_at" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "transaction_hash" }
                },
                { kind: "Field", name: { kind: "Name", value: "atom_id" } },
                { kind: "Field", name: { kind: "Name", value: "triple_id" } },
                { kind: "Field", name: { kind: "Name", value: "deposit_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "redemption_id" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AtomMetadata" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "total_shares"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions"
                                          },
                                          arguments: [
                                            {
                                              kind: "Argument",
                                              name: {
                                                kind: "Name",
                                                value: "where"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "account"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "id"
                                                          },
                                                          value: {
                                                            kind: "ObjectValue",
                                                            fields: [
                                                              {
                                                                kind: "ObjectField",
                                                                name: {
                                                                  kind: "Name",
                                                                  value: "_in"
                                                                },
                                                                value: {
                                                                  kind: "Variable",
                                                                  name: {
                                                                    kind: "Name",
                                                                    value: "addresses"
                                                                  }
                                                                }
                                                              }
                                                            ]
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ],
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account_id"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "id"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "label"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "image"
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "creator" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "FragmentSpread",
                                    name: {
                                      kind: "Name",
                                      value: "AccountMetadata"
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "total_shares"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions"
                                          },
                                          arguments: [
                                            {
                                              kind: "Argument",
                                              name: {
                                                kind: "Name",
                                                value: "where"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "account"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "id"
                                                          },
                                                          value: {
                                                            kind: "ObjectValue",
                                                            fields: [
                                                              {
                                                                kind: "ObjectField",
                                                                name: {
                                                                  kind: "Name",
                                                                  value: "_in"
                                                                },
                                                                value: {
                                                                  kind: "Variable",
                                                                  name: {
                                                                    kind: "Name",
                                                                    value: "addresses"
                                                                  }
                                                                }
                                                              }
                                                            ]
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ],
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account_id"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "id"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "label"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "image"
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "total_shares"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions"
                                          },
                                          arguments: [
                                            {
                                              kind: "Argument",
                                              name: {
                                                kind: "Name",
                                                value: "where"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "account"
                                                    },
                                                    value: {
                                                      kind: "ObjectValue",
                                                      fields: [
                                                        {
                                                          kind: "ObjectField",
                                                          name: {
                                                            kind: "Name",
                                                            value: "id"
                                                          },
                                                          value: {
                                                            kind: "ObjectValue",
                                                            fields: [
                                                              {
                                                                kind: "ObjectField",
                                                                name: {
                                                                  kind: "Name",
                                                                  value: "_in"
                                                                },
                                                                value: {
                                                                  kind: "Variable",
                                                                  name: {
                                                                    kind: "Name",
                                                                    value: "addresses"
                                                                  }
                                                                }
                                                              }
                                                            ]
                                                          }
                                                        }
                                                      ]
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ],
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account_id"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "shares"
                                                }
                                              },
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "account"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "id"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "label"
                                                      }
                                                    },
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "image"
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "deposit" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sender_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sender" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "receiver_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "receiver" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares_for_receiver" }
                      },
                      {
                        kind: "Field",
                        name: {
                          kind: "Name",
                          value: "sender_assets_after_total_fees"
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vault" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_in"
                                                    },
                                                    value: {
                                                      kind: "Variable",
                                                      name: {
                                                        kind: "Name",
                                                        value: "addresses"
                                                      }
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "redemption" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sender_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "sender" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "receiver_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "receiver" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "assets_for_receiver" }
                      },
                      {
                        kind: "Field",
                        name: {
                          kind: "Name",
                          value: "shares_redeemed_by_sender"
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "data" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "emoji" } },
          { kind: "Field", name: { kind: "Name", value: "type" } },
          { kind: "Field", name: { kind: "Name", value: "wallet_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "creator" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "AtomValue" }
          }
        ]
      }
    }
  ]
};
var GetStats = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetStats" },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "stats" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "StatDetails" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "StatDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "stats" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "contract_balance" } },
          { kind: "Field", name: { kind: "Name", value: "total_accounts" } },
          { kind: "Field", name: { kind: "Name", value: "total_fees" } },
          { kind: "Field", name: { kind: "Name", value: "total_atoms" } },
          { kind: "Field", name: { kind: "Name", value: "total_triples" } },
          { kind: "Field", name: { kind: "Name", value: "total_positions" } },
          { kind: "Field", name: { kind: "Name", value: "total_signals" } }
        ]
      }
    }
  ]
};
var GetTags = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetTags" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "subjectId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "predicateId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "ObjectValue",
                  fields: [
                    {
                      kind: "ObjectField",
                      name: { kind: "Name", value: "_and" },
                      value: {
                        kind: "ListValue",
                        values: [
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "subject_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "subjectId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          },
                          {
                            kind: "ObjectValue",
                            fields: [
                              {
                                kind: "ObjectField",
                                name: { kind: "Name", value: "predicate_id" },
                                value: {
                                  kind: "ObjectValue",
                                  fields: [
                                    {
                                      kind: "ObjectField",
                                      name: { kind: "Name", value: "_eq" },
                                      value: {
                                        kind: "Variable",
                                        name: {
                                          kind: "Name",
                                          value: "predicateId"
                                        }
                                      }
                                    }
                                  ]
                                }
                              }
                            ]
                          }
                        ]
                      }
                    }
                  ]
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleMetadata" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "subject_id" } },
          { kind: "Field", name: { kind: "Name", value: "predicate_id" } },
          { kind: "Field", name: { kind: "Name", value: "object_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetTagsCustom = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetTagsCustom" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleMetadata" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "subject_id" } },
          { kind: "Field", name: { kind: "Name", value: "predicate_id" } },
          { kind: "Field", name: { kind: "Name", value: "object_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetTriples = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetTriples" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "total" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleMetadata" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleTxn" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleVaultDetails" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "subject_id" } },
          { kind: "Field", name: { kind: "Name", value: "predicate_id" } },
          { kind: "Field", name: { kind: "Name", value: "object_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "counter_term_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetTriplesWithAggregates = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetTriplesWithAggregates" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "TripleMetadata" }
                      },
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "TripleTxn" }
                      },
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "TripleVaultDetails" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "creator" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "AccountMetadata" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "subject_id" } },
          { kind: "Field", name: { kind: "Name", value: "predicate_id" } },
          { kind: "Field", name: { kind: "Name", value: "object_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "counter_term_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetTriplesCount = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetTriplesCount" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  alias: { kind: "Name", value: "total" },
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetTriple = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetTriple" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "tripleId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triple" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "term_id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "tripleId" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleMetadata" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleTxn" }
                },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "TripleVaultDetails" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AccountMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "accounts" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "label" } },
          { kind: "Field", name: { kind: "Name", value: "image" } },
          { kind: "Field", name: { kind: "Name", value: "id" } },
          { kind: "Field", name: { kind: "Name", value: "atom_id" } },
          { kind: "Field", name: { kind: "Name", value: "type" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "AtomValue" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "atoms" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "value" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "person" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "thing" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "organization" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "name" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "description" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "url" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "account" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "id" } },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "image" } }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "atom" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "image" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "triple" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "counter_term" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "vaults" },
                                    arguments: [
                                      {
                                        kind: "Argument",
                                        name: { kind: "Name", value: "where" },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "curve_id"
                                              },
                                              value: {
                                                kind: "ObjectValue",
                                                fields: [
                                                  {
                                                    kind: "ObjectField",
                                                    name: {
                                                      kind: "Name",
                                                      value: "_eq"
                                                    },
                                                    value: {
                                                      kind: "StringValue",
                                                      value: "1",
                                                      block: false
                                                    }
                                                  }
                                                ]
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ],
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "position_count"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "positions_aggregate"
                                          },
                                          selectionSet: {
                                            kind: "SelectionSet",
                                            selections: [
                                              {
                                                kind: "Field",
                                                name: {
                                                  kind: "Name",
                                                  value: "aggregate"
                                                },
                                                selectionSet: {
                                                  kind: "SelectionSet",
                                                  selections: [
                                                    {
                                                      kind: "Field",
                                                      name: {
                                                        kind: "Name",
                                                        value: "sum"
                                                      },
                                                      selectionSet: {
                                                        kind: "SelectionSet",
                                                        selections: [
                                                          {
                                                            kind: "Field",
                                                            name: {
                                                              kind: "Name",
                                                              value: "shares"
                                                            }
                                                          }
                                                        ]
                                                      }
                                                    }
                                                  ]
                                                }
                                              }
                                            ]
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "subject" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "predicate" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "object" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "data" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "image" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "emoji" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "type" }
                                  },
                                  {
                                    kind: "FragmentSpread",
                                    name: { kind: "Name", value: "AtomValue" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "creator" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "FragmentSpread",
                                          name: {
                                            kind: "Name",
                                            value: "AccountMetadata"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "account" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "id" } },
                { kind: "Field", name: { kind: "Name", value: "label" } }
              ]
            }
          },
          { kind: "Field", name: { kind: "Name", value: "shares" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "total_shares" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "current_share_price" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "PositionAggregateFields" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "positions_aggregate" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "aggregate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "count" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "sum" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleMetadata" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "subject_id" } },
          { kind: "Field", name: { kind: "Name", value: "predicate_id" } },
          { kind: "Field", name: { kind: "Name", value: "object_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "subject" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "predicate" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "object" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "data" } },
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                { kind: "Field", name: { kind: "Name", value: "image" } },
                { kind: "Field", name: { kind: "Name", value: "label" } },
                { kind: "Field", name: { kind: "Name", value: "emoji" } },
                { kind: "Field", name: { kind: "Name", value: "type" } },
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "AtomValue" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "creator" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "FragmentSpread",
                        name: { kind: "Name", value: "AccountMetadata" }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        alias: { kind: "Name", value: "allPositions" },
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: {
                                kind: "Name",
                                value: "PositionAggregateFields"
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionFields" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleTxn" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "block_number" } },
          { kind: "Field", name: { kind: "Name", value: "created_at" } },
          { kind: "Field", name: { kind: "Name", value: "transaction_hash" } },
          { kind: "Field", name: { kind: "Name", value: "creator_id" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "TripleVaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "triples" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "counter_term_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "counter_term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "vaults" },
                  arguments: [
                    {
                      kind: "Argument",
                      name: { kind: "Name", value: "where" },
                      value: {
                        kind: "ObjectValue",
                        fields: [
                          {
                            kind: "ObjectField",
                            name: { kind: "Name", value: "curve_id" },
                            value: {
                              kind: "ObjectValue",
                              fields: [
                                {
                                  kind: "ObjectField",
                                  name: { kind: "Name", value: "_eq" },
                                  value: {
                                    kind: "StringValue",
                                    value: "1",
                                    block: false
                                  }
                                }
                              ]
                            }
                          }
                        ]
                      }
                    }
                  ],
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "FragmentSpread",
                              name: { kind: "Name", value: "PositionDetails" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetAtomTriplesWithPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetAtomTriplesWithPositions" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetTriplesWithPositions = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetTriplesWithPositions" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "triples_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "triples_bool_exp" }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "address" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "String" } }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            alias: { kind: "Name", value: "total" },
            name: { kind: "Name", value: "triples_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "triples" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                { kind: "Field", name: { kind: "Name", value: "term_id" } },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term_id" }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "subject" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "predicate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "object" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } },
                      { kind: "Field", name: { kind: "Name", value: "image" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_ilike"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "address"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "counter_term" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "vaults" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "total_shares" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "position_count" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "positions" },
                              arguments: [
                                {
                                  kind: "Argument",
                                  name: { kind: "Name", value: "where" },
                                  value: {
                                    kind: "ObjectValue",
                                    fields: [
                                      {
                                        kind: "ObjectField",
                                        name: {
                                          kind: "Name",
                                          value: "account_id"
                                        },
                                        value: {
                                          kind: "ObjectValue",
                                          fields: [
                                            {
                                              kind: "ObjectField",
                                              name: {
                                                kind: "Name",
                                                value: "_ilike"
                                              },
                                              value: {
                                                kind: "Variable",
                                                name: {
                                                  kind: "Name",
                                                  value: "address"
                                                }
                                              }
                                            }
                                          ]
                                        }
                                      }
                                    ]
                                  }
                                }
                              ],
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: { kind: "Name", value: "id" }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "image"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetVaults = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetVaults" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "limit" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "offset" }
          },
          type: { kind: "NamedType", name: { kind: "Name", value: "Int" } }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "orderBy" }
          },
          type: {
            kind: "ListType",
            type: {
              kind: "NonNullType",
              type: {
                kind: "NamedType",
                name: { kind: "Name", value: "vaults_order_by" }
              }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "where" }
          },
          type: {
            kind: "NamedType",
            name: { kind: "Name", value: "vaults_bool_exp" }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "vaults_aggregate" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "limit" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "limit" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "offset" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "offset" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "order_by" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "orderBy" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "where" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "where" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "aggregate" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      { kind: "Field", name: { kind: "Name", value: "count" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "nodes" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "atom" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "label" }
                                  }
                                ]
                              }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "triple" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "term_id" }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "subject" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "predicate" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "object" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "term_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        }
                                      ]
                                    }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "positions_aggregate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "nodes" },
                              selectionSet: {
                                kind: "SelectionSet",
                                selections: [
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "account" },
                                    selectionSet: {
                                      kind: "SelectionSet",
                                      selections: [
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "atom_id"
                                          }
                                        },
                                        {
                                          kind: "Field",
                                          name: {
                                            kind: "Name",
                                            value: "label"
                                          }
                                        }
                                      ]
                                    }
                                  },
                                  {
                                    kind: "Field",
                                    name: { kind: "Name", value: "shares" }
                                  }
                                ]
                              }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "current_share_price" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "total_shares" }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      }
    }
  ]
};
var GetVault = {
  kind: "Document",
  definitions: [
    {
      kind: "OperationDefinition",
      operation: "query",
      name: { kind: "Name", value: "GetVault" },
      variableDefinitions: [
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "termId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        },
        {
          kind: "VariableDefinition",
          variable: {
            kind: "Variable",
            name: { kind: "Name", value: "curveId" }
          },
          type: {
            kind: "NonNullType",
            type: {
              kind: "NamedType",
              name: { kind: "Name", value: "numeric" }
            }
          }
        }
      ],
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "Field",
            name: { kind: "Name", value: "vault" },
            arguments: [
              {
                kind: "Argument",
                name: { kind: "Name", value: "term_id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "termId" }
                }
              },
              {
                kind: "Argument",
                name: { kind: "Name", value: "curve_id" },
                value: {
                  kind: "Variable",
                  name: { kind: "Name", value: "curveId" }
                }
              }
            ],
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "FragmentSpread",
                  name: { kind: "Name", value: "VaultDetails" }
                }
              ]
            }
          }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultBasicDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          { kind: "Field", name: { kind: "Name", value: "term_id" } },
          { kind: "Field", name: { kind: "Name", value: "curve_id" } },
          {
            kind: "Field",
            name: { kind: "Name", value: "term" },
            selectionSet: {
              kind: "SelectionSet",
              selections: [
                {
                  kind: "Field",
                  name: { kind: "Name", value: "atom" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      { kind: "Field", name: { kind: "Name", value: "label" } }
                    ]
                  }
                },
                {
                  kind: "Field",
                  name: { kind: "Name", value: "triple" },
                  selectionSet: {
                    kind: "SelectionSet",
                    selections: [
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "term_id" }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "subject" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "predicate" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      },
                      {
                        kind: "Field",
                        name: { kind: "Name", value: "object" },
                        selectionSet: {
                          kind: "SelectionSet",
                          selections: [
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "term_id" }
                            },
                            {
                              kind: "Field",
                              name: { kind: "Name", value: "label" }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          {
            kind: "Field",
            name: { kind: "Name", value: "current_share_price" }
          },
          { kind: "Field", name: { kind: "Name", value: "total_shares" } }
        ]
      }
    },
    {
      kind: "FragmentDefinition",
      name: { kind: "Name", value: "VaultDetails" },
      typeCondition: {
        kind: "NamedType",
        name: { kind: "Name", value: "vaults" }
      },
      selectionSet: {
        kind: "SelectionSet",
        selections: [
          {
            kind: "FragmentSpread",
            name: { kind: "Name", value: "VaultBasicDetails" }
          }
        ]
      }
    }
  ]
};

export { API_URL_DEV, API_URL_LOCAL, API_URL_PROD, AccountAtoms, AccountAtomsAggregate, AccountAtomsAggregateFragmentDoc, AccountAtomsFragmentDoc, AccountMetadata, AccountMetadataFragmentDoc, AccountPositions, AccountPositionsAggregate, AccountPositionsAggregateFragmentDoc, AccountPositionsFragmentDoc, AccountTriples, AccountTriplesAggregate, AccountTriplesAggregateFragmentDoc, AccountTriplesFragmentDoc, AtomMetadata, AtomMetadataFragmentDoc, AtomMetadataMaybedeletethis, AtomMetadataMaybedeletethisFragmentDoc, AtomTriple, AtomTripleFragmentDoc, AtomTxn, AtomTxnFragmentDoc, AtomValue, AtomValueFragmentDoc, AtomVaultDetails, AtomVaultDetailsFragmentDoc, AtomVaultDetailsWithPositions, AtomVaultDetailsWithPositionsFragmentDoc, DepositEventFragment, DepositEventFragmentFragmentDoc, EventDetails, EventDetailsFragmentDoc, FollowAggregate, FollowAggregateFragmentDoc, FollowMetadata, FollowMetadataFragmentDoc, GetAccount, GetAccountDocument, GetAccountMetadata, GetAccountMetadataDocument, GetAccountWithAggregates, GetAccountWithAggregatesDocument, GetAccountWithPaginatedRelations, GetAccountWithPaginatedRelationsDocument, GetAccounts, GetAccountsCount, GetAccountsCountDocument, GetAccountsDocument, GetAccountsWithAggregates, GetAccountsWithAggregatesDocument, GetAtom, GetAtomByData, GetAtomByDataDocument, GetAtomDetails, GetAtomDetailsDocument, GetAtomDocument, GetAtomTriplesWithPositions, GetAtomTriplesWithPositionsDocument, GetAtoms, GetAtomsCount, GetAtomsCountDocument, GetAtomsDocument, GetAtomsWithAggregates, GetAtomsWithAggregatesDocument, GetAtomsWithPositions, GetAtomsWithPositionsDocument, GetConnections, GetConnectionsCount, GetConnectionsCountDocument, GetConnectionsDocument, GetDebugEvents, GetDebugEventsDocument, GetEvents, GetEventsCount, GetEventsCountDocument, GetEventsData, GetEventsDataDocument, GetEventsDocument, GetEventsWithAggregates, GetEventsWithAggregatesDocument, GetFeeTransfers, GetFeeTransfersDocument, GetFollowerPositions, GetFollowerPositionsDocument, GetFollowingPositions, GetFollowingPositionsDocument, GetListDetails, GetListDetailsDocument, GetListDetailsSimplified, GetListDetailsSimplifiedDocument, GetListDetailsWithPosition, GetListDetailsWithPositionDocument, GetListDetailsWithUser, GetListDetailsWithUserDocument, GetListItems, GetListItemsDocument, GetLists, GetListsDocument, GetPosition, GetPositionDocument, GetPositions, GetPositionsCount, GetPositionsCountByType, GetPositionsCountByTypeDocument, GetPositionsCountDocument, GetPositionsDocument, GetPositionsWithAggregates, GetPositionsWithAggregatesDocument, GetSignals, GetSignalsDocument, GetStats, GetStatsDocument, GetTags, GetTagsCustom, GetTagsCustomDocument, GetTagsDocument, GetTriple, GetTripleDocument, GetTriplePositionsByAddress, GetTriplePositionsByAddressDocument, GetTriples, GetTriplesCount, GetTriplesCountDocument, GetTriplesDocument, GetTriplesWithAggregates, GetTriplesWithAggregatesDocument, GetTriplesWithPositions, GetTriplesWithPositionsDocument, GetVault, GetVaultDocument, GetVaults, GetVaultsDocument, GetVerifiedAtomDetails, GetVerifiedAtomDetailsDocument, PinThing, PinThingDocument, PositionAggregateFields, PositionAggregateFieldsFragmentDoc, PositionDetails, PositionDetailsFragmentDoc, PositionFields, PositionFieldsFragmentDoc, RedemptionEventFragment, RedemptionEventFragmentFragmentDoc, StatDetails, StatDetailsFragmentDoc, TripleMetadata, TripleMetadataFragmentDoc, TripleTxn, TripleTxnFragmentDoc, TripleVaultCouterVaultDetailsWithPositions, TripleVaultCouterVaultDetailsWithPositionsFragmentDoc, TripleVaultDetails, TripleVaultDetailsFragmentDoc, VaultBasicDetails, VaultBasicDetailsFragmentDoc, VaultDetails, VaultDetailsFragmentDoc, VaultDetailsWithFilteredPositions, VaultDetailsWithFilteredPositionsFragmentDoc, VaultFieldsForTriple, VaultFieldsForTripleFragmentDoc, VaultFilteredPositions, VaultFilteredPositionsFragmentDoc, VaultPositionsAggregate, VaultPositionsAggregateFragmentDoc, VaultUnfilteredPositions, VaultUnfilteredPositionsFragmentDoc, configureClient, createServerClient, fetcher, useGetAccountMetadataQuery, useGetAccountQuery, useGetAccountWithAggregatesQuery, useGetAccountWithPaginatedRelationsQuery, useGetAccountsCountQuery, useGetAccountsQuery, useGetAccountsWithAggregatesQuery, useGetAtomByDataQuery, useGetAtomDetailsQuery, useGetAtomQuery, useGetAtomTriplesWithPositionsQuery, useGetAtomsCountQuery, useGetAtomsQuery, useGetAtomsWithAggregatesQuery, useGetAtomsWithPositionsQuery, useGetConnectionsCountQuery, useGetConnectionsQuery, useGetDebugEventsQuery, useGetEventsCountQuery, useGetEventsDataQuery, useGetEventsQuery, useGetEventsWithAggregatesQuery, useGetFeeTransfersQuery, useGetFollowerPositionsQuery, useGetFollowingPositionsQuery, useGetListDetailsQuery, useGetListDetailsSimplifiedQuery, useGetListDetailsWithPositionQuery, useGetListDetailsWithUserQuery, useGetListItemsQuery, useGetListsQuery, useGetPositionQuery, useGetPositionsCountByTypeQuery, useGetPositionsCountQuery, useGetPositionsQuery, useGetPositionsWithAggregatesQuery, useGetSignalsQuery, useGetStatsQuery, useGetTagsCustomQuery, useGetTagsQuery, useGetTriplePositionsByAddressQuery, useGetTripleQuery, useGetTriplesCountQuery, useGetTriplesQuery, useGetTriplesWithAggregatesQuery, useGetTriplesWithPositionsQuery, useGetVaultQuery, useGetVaultsQuery, useGetVerifiedAtomDetailsQuery, useInfiniteGetAccountMetadataQuery, useInfiniteGetAccountQuery, useInfiniteGetAccountWithAggregatesQuery, useInfiniteGetAccountWithPaginatedRelationsQuery, useInfiniteGetAccountsCountQuery, useInfiniteGetAccountsQuery, useInfiniteGetAccountsWithAggregatesQuery, useInfiniteGetAtomByDataQuery, useInfiniteGetAtomDetailsQuery, useInfiniteGetAtomQuery, useInfiniteGetAtomTriplesWithPositionsQuery, useInfiniteGetAtomsCountQuery, useInfiniteGetAtomsQuery, useInfiniteGetAtomsWithAggregatesQuery, useInfiniteGetAtomsWithPositionsQuery, useInfiniteGetConnectionsCountQuery, useInfiniteGetConnectionsQuery, useInfiniteGetDebugEventsQuery, useInfiniteGetEventsCountQuery, useInfiniteGetEventsDataQuery, useInfiniteGetEventsQuery, useInfiniteGetEventsWithAggregatesQuery, useInfiniteGetFeeTransfersQuery, useInfiniteGetFollowerPositionsQuery, useInfiniteGetFollowingPositionsQuery, useInfiniteGetListDetailsQuery, useInfiniteGetListDetailsSimplifiedQuery, useInfiniteGetListDetailsWithPositionQuery, useInfiniteGetListDetailsWithUserQuery, useInfiniteGetListItemsQuery, useInfiniteGetListsQuery, useInfiniteGetPositionQuery, useInfiniteGetPositionsCountByTypeQuery, useInfiniteGetPositionsCountQuery, useInfiniteGetPositionsQuery, useInfiniteGetPositionsWithAggregatesQuery, useInfiniteGetSignalsQuery, useInfiniteGetStatsQuery, useInfiniteGetTagsCustomQuery, useInfiniteGetTagsQuery, useInfiniteGetTriplePositionsByAddressQuery, useInfiniteGetTripleQuery, useInfiniteGetTriplesCountQuery, useInfiniteGetTriplesQuery, useInfiniteGetTriplesWithAggregatesQuery, useInfiniteGetTriplesWithPositionsQuery, useInfiniteGetVaultQuery, useInfiniteGetVaultsQuery, useInfiniteGetVerifiedAtomDetailsQuery, usePinThingMutation };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.mjs.map